/**
 * ONE module, ONE exported dictionary, Russian only. No i18n framework at this stage
 * (визуальный контракт); functions where a value is interpolated, so number/word agreement
 * stays in one place.
 *
 * Until 2026-08-17 this file exported four dictionaries (`T`, `TI`, `TR`, `TC`). The split
 * came from four authors editing in parallel, not from a distinction: the same set of images
 * was named «снимок чистый» in the strip legend and «чистый снимок» in the calendar, the
 * strip's own axis bands existed twice under two names, and only three of the four blocks had
 * a dead-key guard. One dictionary, one name per concept, one guard (`strings.test.ts`).
 *
 * THREE LEVELS (правила текстов — ТЗ.md). Every string belongs to exactly one:
 *   1. the screen — professional and completely clear. No NDVI, no thresholds, no band
 *      names, no Sentinel, no pixel counts. Precise ordinary words stay: растительность,
 *      стерня, пашня, всходы, съёмка, контур.
 *   2. «Откуда мы это знаем» / «Что видно на снимках» / «Чего стоит этот ответ» — the
 *      disclosures. NDVI and the measured numbers live here, glossed once.
 *   3. `evidence`, `rejected`, `flags`, `dev`, `reconcile`, `groundTruth` — the instrument.
 *      Jargon here is sharpened, not softened: «прогон», «QA_LEGACY_GRID», «VV / VH / дБ».
 *
 * Two rules that outrank brevity:
 *   * nothing may promise more than 13 of 18 held out, Wilson [0.49, 0.88], +23 pp tuning
 *     optimism, ZERO verified negative fields — there is no false-positive rate at all;
 *   * a harvest has never been observed, only a loss of vegetation, which mowing, trampling,
 *     drying and hail also produce. Since 2026-09-04 the OWNER's headings assert it anyway
 *     («Поле убрано — на земле стерня»), and the price is fixed rather than waived:
 *     the caveat naming the causes the images cannot tell apart stands under the heading
 *     ALWAYS, on the screen, never inside a disclosure (правила текстов — ТЗ.md). Outside
 *     `verdict.heading` the rule is unchanged — no other string may assert harvesting.
 *
 * «Мы» appears only where the sentence confesses OUR limit rather than claiming something
 * about the world (§11.3). Making those impersonal would turn an admission into an assertion.
 */

const plural = (n: number, one: string, few: string, many: string): string => {
  const abs = Math.abs(n) % 100;
  const tail = abs % 10;
  if (abs > 10 && abs < 20) return many;
  if (tail > 1 && tail < 5) return few;
  if (tail === 1) return one;
  return many;
};

export const days = (n: number): string => `${n} ${plural(n, "день", "дня", "дней")}`;
export const dates = (n: number): string => `${n} ${plural(n, "дата", "даты", "дат")}`;
export const fields = (n: number): string => `${n} ${plural(n, "поле", "поля", "полей")}`;
export const shots = (n: number): string => `${n} ${plural(n, "съёмка", "съёмки", "съёмок")}`;
/** Не экспортируется: счёт анализов нужен внутри двух строк словаря и больше нигде. */
const analyses = (n: number): string => `${n} ${plural(n, "анализ", "анализа", "анализов")}`;

/**
 * Одна фраза, один источник: до 2026-08-17 она стояла двумя ключами — один для журнала
 * съёмок, другой для кода отказа сервера, — и расходились бы они молча.
 */
const TOO_SMALL = "Поле слишком мало: на снимке от него остаётся несколько пикселей.";

/**
 * Тем же приёмом, что и `TOO_SMALL`: одна фраза на два входа — цепочку причин вердикта и код
 * отказа сервера. Счёта съёмок в ней с 2026-09-04 нет (UX_SPEC §3.5): знаменатель на экране
 * один, и печатает его блок снимков, а причина стоит в карточке.
 */
const NO_CLEAR_DATES = "Ни один снимок не прошёл проверку.";

/**
 * One phrase for both entrances (vegetation block and the strip): the same 60-day window with
 * zero acquisitions must not be described by two different sentences (strip-empty-unify).
 */
const NO_ACQUISITIONS_60D = "За 60 дней съёмок над этим полем не было.";

/**
 * Заметка без текста — обычное дело: её завёл GPS фотографии, а не человек. Слово одно на два
 * места, где такую заметку приходится назвать, — на подпись в списке привязки и на середину
 * доступного имени пина, — и разойтись им нельзя: это одна и та же заметка в двух списках.
 */
const NO_TEXT = "без описания";

/**
 * Основание заголовка №3, одной фразой на две оговорки: `caveat.tilled` и
 * `caveat.unknownUseTilled`. Разъехаться им нельзя — это единственное место продукта, где
 * сказано, ЧЕМ на самом деле различены «уборка (стерня)» и «обработка почвы»: одной
 * величиной, количеством оставшейся растительности. Копия в двух ключах молча разошлась бы.
 */
const TILLED_BASIS =
  "Пахоту снимок отделяет от стерни грубо — по тому, сколько на поле осталось растительности, " +
  "а не по виду земли: так же выглядит поле, убранное до земли.";

/** Поправка к типу поля, общая у обеих оговорок с непосчитанным типом — тем же приёмом. */
const UNKNOWN_USE_TAIL =
  "Тип поля не определён: если это луг, растительность скосили — тогда не было ни уборки, " +
  "ни пахоты.";

export const T = {
  app: {
    title: "Копатели",
    tabMap: "Карта",
    // `tabReconcile` УДАЛЁН 2026-09-05 (решение владельца): экран сверки ушёл с продуктовой
    // поверхности вместе со всей методологией. Цифра владельца — CSV /api/truth/agreement.csv;
    // слепая форма наблюдений (`groundTruth`) живёт за VERIFICATION_MODE.
    tablistLabel: "Экраны",
    themeToDark: "Тёмная тема",
    themeToLight: "Светлая тема",
    // Виден только когда режим включён: оператор обязан знать, что смотрит на проверочную
    // поверхность, а не на продуктовую.
    verificationMode: "режим верификации",
    logout: "Выйти",
    signedInAs: (login: string) => `Вы вошли как ${login}`,
  },

  // Только вход. Почты в продукте нет нигде, учётные записи владелец заводит скриптом —
  // самостоятельной регистрации не существует.
  auth: {
    subtitle: "Вход по приглашению",
    login: "Логин",
    password: "Пароль",
    submitLogin: "Войти",
    working: "Проверка…",
    invalidCredentials: "Неверный логин или пароль",
    sessionExpired: "Сессия истекла — войдите заново",
    // 2026-10-02: «слежение за полями» ушло из обещания вместе с фичой — экран входа не
    // зовёт в то, чего за ним нет. Названо ровно то, что открывается.
    why: "Карта и анализ полей доступны только после входа",
    noSelfSignup: "Регистрации на сайте нет: логин и пароль выдают вместе с приглашением",
  },

  // Инструменты контура — иконки над картой. Слова живут в `aria-label` и в тултипе кнопки:
  // они доступны скринридеру и наведению, но не напечатаны поверх снимка.
  tools: {
    groupLabel: "Инструменты контура поля",
    draw: "Рисовать",
    // Правится ЧЕРНОВИК до сохранения: геометрию сохранённого поля изменить нельзя, PATCH
    // принимает имя, приоритет, заметку и архив (docs/UI_PLAN.md §6.3).
    edit: "Править контур",
    stop: "Готово",
    clear: "Стереть",
    save: "Сохранить поле",
    delete: "Удалить поле",
    deleteAsk: (name: string) => `Удалить «${name}» вместе со всем, что по нему посчитано?`,
    deleteYes: "Да, удалить",
    // НЕ «Отмена»: во взведённом вопросе удаления рядом стоит «Отмена» карточки поля
    // (`fieldCard.cancel`), и две одинаковые кнопки в одном кадре отменяют разное — одна
    // вопрос, другая всю правку. Обе половины вопроса теперь называют свой исход, как и
    // положено паре «Да, удалить» / «Не удалять».
    deleteNo: "Не удалять",
    area: "Площадь",
    areaPending: "нарисуйте контур",
    hectares: "га",
    nameLabel: "Название",
    namePlaceholder: "например, Гать за мостом",
    nameHint:
      "Вместо названия подставлены координаты. Впишите своё название — по нему поле легко найти в поиске.",
    drawHint:
      "Клик — вершина. Замкнуть: клик в первую точку, двойной клик или Enter. Esc — бросить рисование.",
    editHint:
      "Тяните вершину. Клик в середину стороны — новая вершина. Правый клик по вершине — убрать её.",
    tooBig: (limit: number) => `Больше ${limit} га — анализ по такому полю не запускается.`,
    // Тезис в шапке консоли: одна строка, которая меняет решение ехать. Почему именно так —
    // в раскрытии рядом, а не поперёк ответа.
    smallFieldThesis: (small: number) => `Поле меньше ${small} га — ответ по нему ненадёжен.`,
    smallFieldWhy: "Почему",
    // Одно предупреждение вместо двух: на поле 2 га верны оба следствия. «Порог» и «спекл» —
    // рабочие слова чужой профессии, следствие названо словами, числа не изменились.
    smallField: (small: number, radar: number | null) =>
      radar === null
        ? `Меньше ${small} га: на таком поле спутник видит растительность даже там, где её нет.`
        : `Меньше ${small} га: на таком поле спутник видит растительность даже там, где её нет, а ниже ${radar} га радару мешает собственный шум.`,
  },

  firstRun: {
    title: "Начните с контура поля",
    step: "Обведите поле: кнопка «Рисовать» — первая слева в ряду над картой. Или перетащите на карту файл с контуром — GeoJSON или KML.",
  },

  // Разбор файла контуров. Кнопки «Импорт» больше нет: файл кладут перетаскиванием на карту,
  // а эти строки — единственный отчёт о том, что из него вышло. Виден он после обычного
  // действия пользователя, поэтому внутренности форматов (Placemark, «полигон», текст парсера)
  // на экран не выходят: граница поля называется «контуром» везде. Самостоятельные сообщения —
  // с заглавной; фрагменты, печатаемые после «имя: », — со строчной.
  import: {
    created: (n: number) => `Создано полей: ${n}`,
    skipped: (n: number) => `пропущено: ${n}`,
    nothingToImport: "В файле нет контуров полей.",
    // Не «Импорт 1»: слова «импорт» на экране больше нет ни в одном органе, а имя поля
    // человек всё равно перепишет — оно должно называть происхождение, а не операцию.
    defaultFieldName: (index: number) => `Из файла ${index}`,
    // Raw parser text never reaches the screen; the argument stays for call-site
    // compatibility and for the dev surface to pick up later (import-badjson).
    badJson: (_message: string) =>
      "Файл не прочитан: это не GeoJSON и не KML. Проверьте, что перетащили тот файл.",
    // `${type}` в скобках — цитата из файла пользователя, не наш жаргон.
    notAPolygon: (type: string) => `нужен замкнутый контур поля (в файле — ${type})`,
    badKml: "Файл KML повреждён — не читается.",
    noPlacemarks: "в файле KML нет ни одного объекта с контуром",
    // Без «полигона»: граница поля называется «контуром» везде (см. шапку секции).
    noPolygonInPlacemark: "нет замкнутого контура — внутри точка, линия или пусто",
    // Позиция имени в отчёте о пропущенном контуре: «?» на экране читался сбоем (drop-noname).
    unnamed: "без имени",
  },

  fieldsPanel: {
    title: "Поля",
    none: "Полей нет",
    // Счёт, у которого все поля в архиве. «Полей нет» на нём — ложь: поля есть, они убраны.
    // Орган назван словами кнопки `archiveShow` дословно — тупик расшит (live-none).
    liveNone: "Все поля в архиве. Откройте их кнопкой «Показать архив».",
    // Вторая половина строки списка, и она печатается ТОЛЬКО когда есть что сказать
    // (UX_SPEC §7.4): у поля есть ответ — стоит его дата, были одни безответные попытки —
    // «ответа нет», не было ничего — половины строки нет вовсе (`neverRun` удалён как
    // мёртвый: неанализированная строка молчит).
    //
    // «Ответ» — слово нейтральное к словарю состояний нарочно: в строке шириной с ноготь
    // любое из шести названий состояния («Поле не сеяно (пары)») либо не поместится, либо
    // будет усечено на середине и прочитано наоборот. Сам ответ читается в консоли по клику,
    // здесь сказано только, что он есть и НА какое число: дата — `last_run.analysis_date`,
    // день, НА КОТОРЫЙ считали, а не день, когда нажали кнопку. Счёт попыток убран по
    // глоссарию (runs-no-answer); параметр остался ради живых вызовов.
    lastAnswer: (date: string) => `ответ на ${date}`,
    runs: (_n: number) => "ответа нет",
    // Одно семейство с кнопкой «Анализ: N полей» — коллизия двух «выбрать» снята.
    selected: "К анализу",
    selectAll: "Выбрать все",
    clearSelection: "Снять выбор",
    // Дозагрузка больше не выбрасывает список, поэтому её надо назвать: слот счётчика в шапке
    // печатает не число, а причину, по которой числа сейчас нет.
    updating: "Обновление…",
    staleFailed: "Список не обновился.",
    saved: "Сохранено",
    // Отбора по названию здесь нет: он уже есть в поиске над картой. Чего не хватало — порядка.
    sortLabel: "Порядок",
    sortPriority: "По приоритету",
    sortName: "По названию",
    sortArea: "По площади",
    // Два положения — два слова. Одна подпись на оба оставляла положение защёлки читаемым
    // только цветом охряной рамки `aria-pressed`.
    archiveShow: "Показать архив",
    archiveHide: "Скрыть архив",
    archivedGroup: "В архиве",
    archivedNone: "В архиве пусто",
    // Один раз у группы вместо двадцати `title`: подсказку на двадцати строках никто не
    // читает, а на `disabled`-кнопке она в части движков не появляется вовсе.
    archivedGroupWhy:
      "Поля из архива не анализируются. Верните поле в список, чтобы работать с ним.",
    // Заголовок последней группы списка — для полей, которым группу не назначили. Стоит
    // перед архивом и печатается только тогда, когда хоть одна группа есть: иначе это
    // подпись «без группы» над списком, где групп нет вообще. Капс делает CSS.
    groupNone: "Без группы",
  },

  // Свойства поля, которые ставит человек: имя, очередь поездки и его собственная причина.
  // Всё остальное, что продукт про поле знает, он измерил сам.
  fieldCard: {
    // Одно имя действия, и на экране его нет: вставляемой полосы действий не существует, а в
    // строке списка на зарезервированном месте стоит карандаш (UX_SPEC §13.2). Слово живёт в
    // `aria-label` и `title` — там же, где глаголы всех остальных иконок. Короткая форма
    // «Изменить» удалена вместе с той полосой: она была подписью на кнопке, которой нет.
    edit: "Изменить поле",
    nameLabel: "Название",
    namePlaceholder: "например, Гать за мостом",
    priorityLabel: "Приоритет",
    // Три ступени, третья — отсутствие приоритета. Свободное число было бы очередью, которую
    // никто не перенумеровывает; «Еду» в русском двоится (ехать / есть) и звучит репликой, а
    // не свойством строки списка.
    //
    // Нижняя ступень звалась «Без метки» ради ширины — 106 px против 191 px в полосе действий
    // строки. Полосы больше нет (UX_SPEC §13.2), в строке эта ступень не печатается вовсе
    // («Без метки» на каждой из двадцати строк — шум), и осталась она значением кнопки-цикла в
    // карточке, где места достаточно. А с 2026-09-22 экономия ещё и стала вредной: точка на
    // карте теперь «Заметка», и «Без метки» рядом с ней читается как «заметки нет». Возвращено
    // слово, которым владелец назвал ступень с самого начала.
    priority: {
      go: "Высокий",
      later: "Средний",
      none: "Без приоритета",
    },
    // Кнопка-цикл без подсказки читалась выпадашкой, которая не раскрылась; ступени названы
    // дословно значениями `priority`, орган «Сохранить» — словами кнопки (priority-hint).
    priorityHint:
      "Нажатие меняет по кругу: Высокий → Средний → Без приоритета. Сохраняется сразу, без «Сохранить».",
    // Группа — свободное имя, которое человек пишет сам: областей и районов у нас нет
    // справочником, а при квоте в 20 полей их и не потребуется. Одно и то же имя собирает
    // поля в одну группу, поэтому подсказка показывает уже занятые написания.
    groupLabel: "Группа",
    groupPlaceholder: "например, Тверская обл.",
    groupHint: (names: string) => `Уже есть: ${names}. Одно и то же имя сводит поля в одну группу.`,
    // «Заметка» → «Описание поля» (2026-09-22, unified-notes). Слово уступило точке на карте:
    // там оно называет то, что человек и правда заводит, пишет и удаляет поштучно, а здесь —
    // текст, который у поля ровно один и живёт в его карточке. Прежний запрет («точка на карте
    // не может зваться заметкой, потому что заметка занята») снят этим же решением: занятым
    // оказалось не то слово.
    noteLabel: "Описание поля",
    notePlaceholder: "чем интересно: что видно на старых картах, как подъехать",
    save: "Сохранить",
    saving: "Сохранение…",
    cancel: "Отмена",
    // Вопрос на месте, а не диалог: тем же приёмом сделано удаление поля (DrawTools).
    unsavedTitle: "Изменения не сохранены",
    discard: "Закрыть без сохранения",
    // Архив и удаление стоят одним блоком в раскрытой карточке: оба убирают поле из списка,
    // и выбирают между ними один раз. Заголовок называет общий исход, а не одно из действий.
    lifecycleLabel: "Убрать поле",
    archive: "В архив",
    archiveHint:
      "Поле уходит из списка и освобождает место для нового. Всё, что по нему посчитано, остаётся. В архиве оно не анализируется.",
    // Второй выход из списка, необратимый. Число анализов стоит в подсказке не для красоты:
    // именно оно и пропадает, а по строке списка этого не видно. При нуле ветка своя —
    // «это 0 анализов» пугало бы там, где терять нечего.
    deleteHint: (runs: number) =>
      runs === 0
        ? "Поле удаляется навсегда. Место в списке освободится, как и после архива."
        : `Поле удаляется навсегда — вместе со всем, что по нему посчитано, а это ${analyses(runs)}. ` +
          `Место в списке освободится, как и после архива.`,
    // Строка-подтверждение вместо исчезнувшей строки: без неё удаление читается как сбой.
    deleted: "Поле удалено",
    unarchive: "Вернуть в список",
    // Подтверждение стоит там, где нажали: строка на глазах переезжает в группу архива и
    // несёт на себе «Вернуть в список».
    archivedDone: "Поле в архиве.",
    archivedTag: "в архиве",
    archivedRowWhy:
      "Поле в архиве: анализ по нему не запускается. Верните в список, чтобы работать с ним.",
    // Своя строка, а не английское сообщение сервера: английского на продуктовой поверхности
    // не бывает (правила текстов — ТЗ.md). Названо и то, что делать.
    quotaFull: "Свободных мест для полей нет. Уберите в архив другое поле или удалите ненужное.",
  },

  // ЗАМЕТКА — точка на карте, у которой бывает текст, бывают фотографии, а бывает и то и
  // другое (2026-09-22, unified-notes). До этого дня здесь была «метка», а «заметкой» звался
  // текст поля, и порядок слов был обратный: точка не могла взять имя, потому что оно занято.
  // Решение владельца поменяло местами не ключи, а то, чем эти две вещи стали: точка обросла
  // содержимым и перестала быть пометкой, а текст поля остался одной строкой в карточке и
  // честнее зовётся описанием (`fieldCard.noteLabel`).
  //
  // Ключи узла НЕ переименованы: `T.markers.*` — это адрес в коде, и менять его ради слова на
  // экране значит переписать полсотни мест ради ничего. Слова внутри — все до одного новые.
  //
  // Все строки говорят «от поля», а не «на поле»: заметка законно стоит за контуром — съезд,
  // брод, место находки.
  markers: {
    title: "Заметки",
    add: "Заметка",
    // Лист под тулбаром, пока режим включён. Предел назван сразу: узнать о нём из отказа,
    // уже ткнув в карту, — значит потерять клик и не понять, почему.
    hint: (meters: number) =>
      `Клик по карте ставит заметку. Дальше ${meters} м от поля она не ставится. Esc — выйти.`,
    placeholder: "Что здесь? (съезд, машина, находки…)",
    edit: "Изменить",
    delete: "Удалить",
    // Удаление заметки с фотографиями — и с 2026-09-22 (notes-v2) оно их УНОСИТ. Прежняя
    // строка обещала обратное («3 фото останутся в блоке „Фото поля“»), и обещание было
    // честным ровно до того дня, когда фотография вне заметки перестала существовать. Такую
    // строку нельзя было оставить смягчённой: она отвечает на вопрос «потеряю ли я снимки»,
    // и любой ответ, кроме прямого, здесь дороже всего остального в словаре.
    deleteAsk: (photos: number) =>
      photos === 0
        ? "Удалить заметку?"
        : `Удалить заметку? Вместе с ней ${plural(photos, "удалится", "удалятся", "удалятся")} ${photos} фото.`,
    // Отказы. Оба называют предел числом: без числа «слишком далеко» не говорит, куда двигать.
    tooFar: (meters: number) => `Слишком далеко: заметка ставится не дальше ${meters} м от поля.`,
    limit: (max: number) => `Больше ${max} заметок на поле не ставится. Удалите ненужную.`,
    // Пустую заметку завести нельзя, но СТАТЬ пустой она может, и пин остаётся. Поэтому строка
    // называет оба способа её наполнить, а не требует текста: фотография — такое же содержимое.
    needText: "Напишите, что здесь, или добавьте фото.",

    // ─── Карточка, модалка, доступные имена (notes-v4) ───────────────────────────────────
    // `move` и `moveHint` УДАЛЕНЫ 2026-09-23: перетаскивания заметки нет вовсе, место меняют
    // через place-режим (`placing`, `placeAgain`), а он называет себя сам.
    //
    // Карандаш и корзина на карточке — иконки без подписи, и в списке из двадцати карточек их
    // доступные имена обязаны различаться. Голое «Изменить» скринридер произносит двадцать раз
    // подряд одинаково, и человек, слушающий список, не знает, на которой из них стоит.
    editAria: (date: string) => `Изменить заметку от ${date}`,
    deleteAria: (date: string) => `Удалить заметку от ${date}`,
    // Доступное имя окна. Оно же отвечает на «что у меня открылось» тому, кто пришёл сюда
    // клавишами, а не мышью: заголовок в окне — дата, и имя говорит ту же дату теми же словами.
    modalLabel: (date: string) => `Заметка от ${date}`,
    // Крестик закрывает ЗАМЕТКУ, а не фотографию: окно и есть заметка целиком — галерея,
    // комментарий и дата. «Закрыть фото» (`photos.close`) ушло вместе с отдельным просмотром.
    close: "Закрыть заметку",
    // Рельса миниатюр под большим кадром. Имя называет и предмет, и границу: это кадры ЭТОЙ
    // заметки, а не всего поля, — иначе счётчик «3 / 4» читается как поломка.
    photoRail: "Фото этой заметки",
    // Заметка без единого кадра — обычное дело: её могли завести текстом. Строка только
    // сообщает, а приглашение стоит рядом на кнопке; звать дважды в одном окне не нужно.
    noPhotos: "Фотографий пока нет.",

    // ─── Черновик ────────────────────────────────────────────────────────────────────────
    // Файлы, выбранные до сохранения: они ещё никуда не отправлены, и ячейки в ленте стоят
    // штриховые. Строка объясняет, почему их не видно среди остальных фото поля.
    draftPhotos: (n: number) =>
      `${n} ${plural(n, "фото добавится", "фото добавятся", "фото добавятся")} после сохранения.`,
    // Крестик на ячейке ВЫБРАННОГО, но ещё не отправленного файла. НЕ «Удалить фото»: удалять
    // нечего — файл никуда не уехал, и обещание удаления пугало бы последствием, которого не
    // существует. «Убрать» — домашний глагол словаря ровно для этого случая: так же сказано
    // про место заметки, про метку поиска и про поле («Убрать место», «Убрать метку», «Убрать
    // поле») — всюду, где вещь уходит с глаз, но не уничтожается.
    //
    // Фраза верна только про невыбранный ещё файл, и в этом её защита: у сохранённой
    // фотографии «убрать из заметки» некуда — вне заметки её не бывает, там работает
    // `photos.delete`. Два крестика рядом выглядят одинаково, и различают их только эти слова.
    draftRemove: "Убрать из заметки",

    // ─── Панель заметок в консоли (notes-v2) ─────────────────────────────────────────────
    // Панель — теперь главный вход к заметкам: блок «Фото поля» её не пересиливает, потому
    // что фотографии вне заметки больше не существует. Пустое состояние переехало сюда же, и
    // приглашение в нём говорит про ЗАМЕТКУ, а не про снимок: снимок стал её содержимым.
    panelEmpty: "Заметок нет",
    emptyWhy: "Следы техники, колея, находки — то, что видно только на месте.",
    // Третья строка пустой панели, и с 2026-09-23 она единственное на весь продукт упоминание
    // того, что сервер раскладывает пачку сам: кнопка шапки отменена, и другого места сказать
    // про это не осталось (UX_NOTES4 §7). Поэтому строка выросла — она обязана назвать не
    // только жест, но и ИСХОД, иначе человек бросает восемь кадров в пустоту и ждёт, что
    // будет.
    //
    // Исходов ровно два, и оба названы: у кадра либо есть место съёмки, либо нет. Обещать
    // «разойдутся по местам» целиком было бы враньём для половины пачки — телефон ловит место
    // не всегда, и такие кадры складываются в одну заметку поездки, а не пропадают.
    dropHint:
      "Фото можно перетащить сюда: те, что помнят место, встанут на карту сами, остальные " +
      "лягут одной заметкой.",
    // ВТОРАЯ дроп-зона, и вторая строка к ней — в окне заметки. Жест тот же, ИСХОД другой и
    // единственный: файлы ложатся в эту самую заметку, никакой раскладки по местам съёмки нет.
    // Печатать здесь `dropHint` было бы обещанием поведения, которого на этой поверхности не
    // существует, — поэтому ключа два, и различаются они ровно тем, что обещают.
    //
    // Глагол общий с `dropHint` («лягут») нарочно: жест один, и человек, видевший обе зоны,
    // должен читать разницу в том, КУДА, а не в том, каким словом это названо.
    dropToNote: "Фото можно перетащить сюда — все лягут в эту заметку.",
    // Кнопка панели. «Заметка» на тулбаре карты ставит точку кликом, а эта заводит заметку без
    // места — разные действия, и глагол их различает.
    addNote: "Новая заметка",
    // Свёрнутая строка заметки: дата и начало текста. Заметка без текста называет себя тем же
    // словом, что и везде, — иначе в списке из пяти строк одна окажется пустой.
    peek: (date: string, what: string) => `${date} — ${what}`,
    // `more` («Ещё 7») УДАЛЁН, не дожив до экрана: панель переехала на собственную вкладку и
    // перестала делить высоту с анализом, а на своей высоте резать список не для чего —
    // двадцать заметок это предел поля, а не длинная лента.
    // Счёт фотографий в свёрнутой карточке. «Фото» не склоняется, поэтому меняется только
    // числительное, и строка остаётся короткой на любой цифре.
    photosCount: (n: number) => `${n} фото`,

    // ─── Место заметки ───────────────────────────────────────────────────────────────────
    // Три действия и намеренная асимметрия в словах: место УКАЗЫВАЮТ (его может не быть
    // вовсе) и УБИРАЮТ (заметка остаётся). «Удалить место» здесь было бы четвёртым «удалить»
    // на одной карточке — рядом с удалением фотографии и самой заметки.
    noPlace: "Указать место",
    placeAgain: "Указать заново",
    placeClear: "Убрать место",
    // Показ одной заметки и всех сразу. Разные кнопки в разных местах, поэтому и слова разные:
    // «все» в первой читалось бы как «эта и ещё какие-то».
    onMap: "Показать на карте",
    showAllOnMap: "Показать все на карте",
    // Лист под тулбаром, пока указывают место уже существующей заметке. Называет, ЧЬЁ место
    // указывают: в режиме, начатом из карточки, человек уже не видит, какую из пяти он открыл.
    placing: (what: string, meters: number) =>
      `Клик по карте укажет место для заметки «${what}». Дальше ${meters} м от поля — нельзя. Esc — отмена.`,
    // То же состояние, но в самой карточке: она остаётся на экране и обязана сказать, чего
    // ждёт, — иначе выглядит замершей.
    placingCard: "Укажите место на карте",

    // ─── Дата заметки ────────────────────────────────────────────────────────────────────
    // Дата — КОГДА ЧЕЛОВЕК БЫЛ НА ПОЛЕ, а не когда загрузил файлы: сервер берёт её из первого
    // снимка, но правит человек. Подсказка существует ровно потому, что это различие нельзя
    // угадать, глядя на цифру.
    dateLabel: "Дата заметки",
    dateHint: "Когда вы были на поле. Можно изменить.",

    // ─── Имя заметки там, где её не видно ────────────────────────────────────────────────
    // Одно слово на три места: свёрнутая строка панели, подпись в списке переноса и середина
    // доступного имени. Заметка без текста — обычное дело (её завело фото, помнящее место),
    // и звать её «пустой» неверно: в ней есть фотография, нет описания.
    noText: NO_TEXT,
    // Пометка на строке списка «Перенести в заметку», где фотография лежит прямо сейчас. Два
    // слова, потому что строка уже несёт дату, счёт фото и картинку, — третьей длинной вещи
    // там не поместится. Без пометки список честно предлагает перенести фотографию туда, где
    // она и так лежит, и нажатие не делает ничего: человек ищет поломку там, где её нет.
    photoIsHere: "сейчас здесь",
    // Доступное имя пина. Хвост про фотографии появляется, только когда они есть: «фото: 0»
    // сказало бы, что их ждут.
    aria: (date: string, text: string | null, photos: number) => {
      const what = text === null || text === "" ? ` ${NO_TEXT}` : `: ${text}`;
      return photos === 0 ? `Заметка ${date}${what}` : `Заметка ${date}${what}, фото: ${photos}`;
    },
  },

  // Фотографии поля, снятые человеком на месте. Блок в консоли между историей и продвинутым.
  //
  // ГЛАВНОЕ ПРАВИЛО УЗЛА: здесь не бывает слова «снимок». Глоссарий вычитки закрепил его за
  // изображением со спутника, а «съёмку» — за пролётом (§15.1), и на одном экране с полосой
  // состояния, календарём и кнопкой «Показать снимок» второе значение того же слова читалось
  // бы как то же самое: «Удалить снимок» рядом с «Показать снимок» — это про разные вещи и
  // про разные последствия. Человеческая фотография называется «фото» во всех падежах и
  // ключах, включая заимствованное из ТЗ предложение под пустым блоком.
  //
  // Даты здесь — общий `formatFull`, и он единственный, кому фотографии дают настоящую
  // работу: год он печатает только у прошлых лет, а фото — единственное на экране, что
  // переживает сезон. Всё остальное считается в окне 60 дней, где год подразумевается.
  photos: {
    title: "Фото поля",
    add: "Добавить фото",
    // `empty` и `emptyWhy` УДАЛЕНЫ 2026-09-22 (notes-v2): пустого блока фотографий больше не
    // бывает — фотография живёт внутри заметки, и пусто теперь у панели заметок, которая про
    // это и говорит (`markers.panelEmpty`).

    // ─── Очередь загрузки ────────────────────────────────────────────────────────────────
    queue: (done: number, total: number) => `Загружено ${done} из ${total}`,
    queueDone: "Готово",

    // ИСХОД КАЖДОГО ФАЙЛА, у которого в EXIF нашлись координаты. Сервер решает, что с ними
    // делать, и три его решения — три разных ответа человеку, который ждёт увидеть точку на
    // карте. Молчит очередь ровно в одном случае: координат в файле не было вовсе, и обещать
    // там нечего.
    //
    // Ни одна из трёх не говорит «EXIF», «GPS» и «координаты»: человек снимал телефоном и
    // знает про это ровно то, что снимок «помнит место». Сказано следствие.
    placedOnMap: "Поставлено на карту",
    // ПАЧКА. Снимки, у которых места нет (телефон не поймал) или оно далеко от поля, не
    // пропадают и не валяются поодиночке: все они складываются в ОДНУ заметку — ту, что
    // человек привёз из этой поездки. Причина названа, потому что она разная и человек её
    // проверяет глазами: «нет места» — вопрос к телефону, «далеко» — вопрос к тому, где стоял.
    //
    // Заметка «создана» она или «дополнена» — разница серверная (`batch_created` против
    // `batch_attached`) и человеку не видна: он в обоих случаях получает одну заметку поездки.
    // Двух строк на это не заводим.
    batchNoFix: "Фото не помнит места — сложено в заметку поездки",
    batchFar: "Снято далеко от поля — сложено в заметку поездки",
    // Больше не исход, а ОТКАЗ (notes-v2): раньше фотография принималась и просто оставалась
    // без точки, теперь класть её некуда — заметки кончились. Поэтому названо и то, что файл
    // не загружен, и то, что удалять надо заметку, а не фотографию.
    notesQuotaFull: (max: number) =>
      `Заметок уже ${max} — фото не загружено. Удалите ненужную заметку.`,

    // ─── Отказы. Каждый называет предел ЧИСЛОМ, как у меток: без числа «слишком большое»
    // не говорит, что делать со следующим файлом. Имя файла стоит первым — при пакетной
    // загрузке иначе не понять, какой именно из двадцати не прошёл.
    notImage: (name: string) => `${name} — это не изображение. Нужен JPEG, PNG или HEIC.`,
    tooLarge: (name: string, mb: number, maxMb: number) =>
      `${name} — ${mb} МБ. Больше ${maxMb} МБ не загружается.`,
    limit: (max: number) => `Больше ${max} фото на поле не загружается. Удалите ненужное.`,
    // Второй предел — на ОДНУ заметку (notes-v2, 409 `too_many_photos_note`). От первого он
    // отличается не числом, а тем, где освобождать: на поле места ещё сколько угодно, кончилось
    // оно у этой заметки. Не сказать этого — значит отправить человека чистить не то.
    limitNote: (max: number) =>
      `В одной заметке больше ${max} фото не хранится. Удалите лишнее здесь или заведите новую заметку.`,
    // `limitAccount` УДАЛЁН 2026-09-22 вместе с самим потолком аккаунта (решение владельца,
    // B4): второго предела больше нет, а строка про «место на других полях» без него
    // отправляла бы человека искать несуществующую причину.
    // Под погасшей кнопкой, и потому НЕ повторяет отказ выше: кнопка уже не зовёт нажимать,
    // объяснить осталось только, почему. Отказ при этом остаётся живым — файлы кладут ещё и
    // перетаскиванием, мимо кнопки.
    limitHint: (max: number) => `Все ${max} мест заняты.`,
    uploadFailed: (name: string) => `${name} не загрузилось.`,
    // Не «попробуйте позже»: пробовать нечего, пока место не освободят на сервере, и ложное
    // приглашение стоило бы человеку ещё одной загрузки на мобильном интернете.
    storageFull: "Свободного места на сервере нет. Фото пока не загружаются.",

    // ─── Просмотр ────────────────────────────────────────────────────────────────────────
    // Доступные имена кнопок-иконок: каждое называет ПРЕДМЕТ, иначе «Закрыть» в лайтбоксе и
    // «Закрыть» у любой другой панели звучат для скринридера одинаково.
    // `close` («Закрыть фото») УДАЛЁН 2026-09-23 (notes-v4): крестик закрывает не кадр, а
    // заметку целиком — имя переехало в `markers.close`.
    prev: "Предыдущее фото",
    next: "Следующее фото",
    // Словами — для скринридера. Видимый счётчик остаётся цифрами через косую черту («3 / 12»):
    // русского в нём нет, ключ ему не нужен, а моноширинный ряд читается с одного взгляда.
    position: (index: number, total: number) => `Фото ${index} из ${total}`,
    delete: "Удалить фото",
    deleteAsk: (date: string) => `Удалить фото от ${date}?`,
    // `showNote` («Показать заметку») УДАЛЁН 2026-09-23 (notes-v4): показывать заметку из
    // заметки незачем — окно ею и является. Дорога на карту осталась одна, с карточки
    // (`markers.onMap`).

    // ─── Перенос между заметками (полоса лайтбокса) ──────────────────────────────────────
    // «Привязать» сменилось на «Перенести» (notes-v2), и это не синоним: фотография больше не
    // бывает непривязанной, она всегда ЧЬЯ-ТО, — значит выбирают не связь, а новую заметку
    // вместо прежней. Пункта «Без места» в списке нет по той же причине: выйти из заметки
    // некуда.
    attachLabel: "Перенести в заметку",

    // УДАЛЕНЫ 2026-09-22 (notes-v2), каждый вместе со своим органом:
    //   `edit` («Изменить подпись») и `notePlaceholder` — подписи у фотографии больше нет,
    //     комментарий один и принадлежит заметке;
    //   `dateLabel` — лайтбокс дат не правит, дата переехала к заметке (`markers.dateLabel`);
    //   `scopeNote` — лайтбокс всегда листает одну заметку, и постоянная подпись об этом
    //     стала шумом: рядом со счётчиком стоит сам текст заметки;
    //   `attachNone` («Без места») — выбор, которого больше не существует;
    //   `empty`, `emptyWhy`, `notesOnMap`, `showOnMap`, `attachedAria` — блок фотографий
    //     перестал быть самостоятельным, и всё, что он о себе рассказывал, говорит панель
    //     заметок (`markers.*`). Глиф привязи не нужен там, где привязаны все.
  },

  // ОДНО имя действия на весь продукт — «анализ». «Посмотреть поле» называло глазами то, что
  // делает расчёт, и распадалось на четыре разных слова в производных строках («просмотров»,
  // «не просмотрено», «просматривается», «прогоны»); последнее ещё и запрещено на ярусе 1.
  // Существительное вместо глагола выбрано ради согласования: «анализов: 3», «не
  // анализировано», «Прошлые анализы» — одна основа во всех формах.
  run: {
    start: "Анализ поля",
    // S4: прогон по полю уже есть, и кнопка перестаёт быть первым знакомством с ним. Глагол
    // тот же — действие не другое, оно повторное.
    startAgain: "Анализ заново",
    // S6: прошлый анализ упал. «Повторить» без предмета уже занято общей кнопкой отказа
    // (`common.retry`), а здесь повторяется именно анализ, и это единственная охра на экране.
    retry: "Повторить анализ",
    // Кнопка И ЕСТЬ индикатор: пока идёт анализ, состояние читается там, где на него нажали,
    // и больше нигде (UX_SPEC §2). До 2026-09-04 ту же фразу печатали ещё три места.
    // Шага может не быть вовсе — сервер присылает первый не мгновенно.
    busy: (step: string | null, seconds: number) =>
      step === null ? `Идёт анализ · ${seconds} с` : `Идёт анализ · ${step} · ${seconds} с`,
    waitingServer: "Ожидание первого шага.",
    failed: "Анализ не удался",
    // История анализов — ярус 1: она отвечает на «когда я это смотрел в прошлый раз» и от
    // режима «для продвинутых» не зависит. Строка-заглядывание печатает дату и ярлык
    // ПРЕДЫДУЩЕГО ответа; «тот же/изменился» не считается и не обещается.
    history: "Анализы по полю",
    // Префикс обязателен, и это не украшение подписи. Без него строка «22 июля — Поле не
    // сеяно (пары)» стоит под заголовком карточки такой же по составу — дата и ярлык — и
    // читается свежим ответом, спорящим с ним (history-peek). Названо, чей это ответ, а
    // «предыдущий» стоит первым словом, до всякой даты.
    historyPeek: (date: string, label: string) => `Предыдущий анализ: ${date} — ${label}`,
    // ЯРУС 3 отсюда и ниже: пакетный прогон, дата анализа и очередь — прибор владельца,
    // и слово «прогон» здесь точное.
    startBatch: (n: number) => `Анализ: ${fields(n)}`,
    startBatchNote: "Прогонит отмеченные поля по очереди — с этой датой и этими флагами.",
    analysisDate: "Дата анализа",
    analysisDateNote:
      "Прогон считает так, будто сегодня эта дата: окно 60 суток отсчитывается назад от неё. По умолчанию — сегодняшняя.",
    queue: "Очередь прогонов",
    queued: "в очереди",
    inProgress: "идёт",
    done: "готово",
    error: "ошибка",
    cancelQueue: "Очистить очередь",
    geeCalls: "вызовов GEE",
  },

  // Правая колонка целиком: две вкладки вместо одного бесконечного столбца (notes-v2). Анализ
  // и заметки отвечают на разные вопросы — «что со мной поле» и «что я там видел», — и второй
  // перестал проматываться мимо первого.
  //
  // Это ВТОРОЙ `role=tablist` на экране, после экранов приложения, и подписи у них разные не
  // для красоты: скринридер объявляет полосы одну за другой, и две «Экраны» подряд не сказали
  // бы, какая из них какая. Счёт заметок к ярлыку не приклеивается — он стоит отдельной
  // цифрой рядом, иначе имя вкладки менялось бы при каждой загрузке фото.
  console: {
    tabAnalysis: "Анализ",
    tabNotes: "Заметки",
    // Не «Вкладки» и не «Разделы»: подпись отвечает на тот единственный вопрос, который
    // человек задаёт полосе, — что сейчас показано в колонке.
    tabsLabel: "Что показано",
  },

  // Состояния колонки, у которых своей карточки нет: поле не выбрано и поле не анализировано.
  // Заголовок называет, чего не хватает, строка под ним — что это даёт или что нажать. Оба
  // обязаны отличаться от «Нет чистых снимков спутника»: там ответа не будет по погоде, здесь
  // его ещё не считали.
  // Строки-обычая «Стоящие посевы и многолетние травы не копают» здесь БОЛЬШЕ НЕТ
  // (2026-09-04). Запрет переехал в скобки заголовков №1 и №4 — «(копать запрещено)», — а
  // обычай рядом с прямым запретом читался бы второй, более слабой формулировкой того же.
  // «Поле анализируется» здесь тоже БОЛЬШЕ НЕТ (2026-09-04, UX_SPEC §12): одна фраза стояла
  // в четырёх местах сразу, и осталась она на кнопке — `run.busy`.
  blocks: {
    noField: "Поле не выбрано",
    // Приглашение вместо тупика: без выбранного поля колонке нечего сказать, но есть чему
    // научить — способов выбрать два, и оба неочевидны.
    noFieldInvite: "Выберите поле в списке слева или щёлкните по контуру на карте.",
    // «Ещё» и есть всё различие между «мы не смотрели» и «смотреть нечего»: без него
    // заголовок читается как приговор полю, а не как состояние нашей работы.
    noRun: "Анализа ещё не было",
    // Одно предложение о том, ЧТО даёт анализ. Обещано ровно измеримое: сход растительности
    // и его дата — не уборка и не проходимость.
    noRunInvite: "Анализ покажет, сошла ли с поля растительность и когда это случилось.",
    // Полоса над прежним ответом, пока считается следующий (S5). Она обязана сказать, почему
    // ответ на экране старый: иначе идущий анализ читается как уже показанный результат.
    newRunBanner: "Идёт новый анализ — пока на экране прежний ответ.",
  },

  // Клиентский выключатель чисел, отдельный от `dev` и без горячей клавиши: числа под ответом
  // спрашивал не тот, кто ищет ошибку пайплайна, а тот, кому мало одного вердикта.
  // С 2026-09-04 переключатель стоит в ВЕРХНЕЙ панели рядом с тёмной темой (UX_SPEC §4.1), и
  // подписи «показать/скрыть» ему больше не нужны: положение несёт `aria-pressed`, как у
  // остальных защёлок. Служебные данные из перечисления ушли — подвал `DevPanel` с этого дня
  // висит на `devMode`, а не на «для продвинутых» (§4.2).
  advanced: {
    // Подпись защёлки. С 2026-09-04 — слова ВЛАДЕЛЬЦА из ТЗ, и это сознательное исключение
    // из правила §1 «подпись называет содержимое, а не уровень читателя»: до этого дня здесь
    // стояли «Числа». Правило не отменено — исключение одно, поимённо, и цена у него та, что
    // подпись больше не говорит, что откроется. Поэтому `what` обязан оставаться на `title`:
    // он и есть единственное место, где перечислено содержимое режима.
    chip: "Продвинутый режим",
    // Заголовок последнего блока колонки. «Провенанс» — слово прибора, на продуктовой
    // поверхности его нет; блок отвечает ровно на «чем посчитан этот ответ».
    blockTitle: "Чем посчитан ответ",
    // Одна строка на два места: `title` защёлки и подпись под заголовком блока ⑤. Называет
    // СОДЕРЖИМОЕ, а не уровень читателя (правила текстов — ТЗ.md).
    // Перечисление называет раскрытия ИХ НАПЕЧАТАННЫМИ словами. «Насколько точен ответ» ушло
    // из перечисления 2026-09-05 вместе с самим раскрытием (`verdict.worth*` удалены, см.
    // (правила текстов — ТЗ.md): подпись не смеет обещать раздел, которого на экране нет.
    what: "Числа под ответом, что видно на снимках и чем он посчитан.",

    // ТРИ СТРОКИ ЗАЩЁЛКИ СЛЕЖЕНИЯ УДАЛЕНЫ 2026-10-02 вместе с самой фичой (решение владельца).
    // Они были единственным местом, где показание `watch_status` вообще выходило на экран, —
    // и выходило с оговоркой про отставание до трёх недель. Без кнопки, которая эту защёлку
    // взводит, строка отчитывалась бы о приборе, которого человек не включал.

    // `devHint` УДАЛЁН (2026-09-05, dev-hint-delete): реклама служебного ящика копателю не
    // адресована, вход по Ctrl+Alt+D остаётся известен оператору из `dev.hint`. Отмена строки
    // §14.10 зафиксирована в (правила текстов — ТЗ.md)
  },

  // Тип поля — ячейка 1, три года истории. Отдельный вопрос: не «убрано ли поле», а «что это
  // за поле». Свой расчёт со своей кнопкой; после удачного анализа сессия запускает его сама,
  // но привязывать его словами к анализу нельзя (fielduse-hint): «Определяется при первом
  // анализе» стояло на поле, у которого анализ УЖЕ был, а тип посчитан не был, — и рядом с
  // типом «Луг» на поле, у которого анализа не было вовсе.
  fieldUse: {
    notComputed: "Тип поля не определён",
    // Называет кнопку её точными словами: у непосчитанного типа она подписана «Определить
    // тип поля» (Console.tsx, field-use-none).
    notComputedHint:
      "Считается по трём годам съёмок — запустите расчёт кнопкой «Определить тип поля».",
    // Одна кнопка, две подписи по факту на экране: «Пересчитать» над непосчитанным типом
    // обещало расчёт, которого не было (fielduse-hint).
    compute: "Определить тип поля",
    recompute: "Пересчитать тип поля",
    running: "Определяется тип поля…",
    // Оба отказа называют орган рядом — кнопку «Определить/Пересчитать тип поля» строкой ниже.
    // Сырой текст сервера на экран не выходит (правила текстов — ТЗ.md); параметр остался
    // ради живых вызовов, читает его поверхность dev.
    timedOut: "Сервер не ответил — тип поля не посчитан. Нажмите кнопку ниже ещё раз.",
    failed: (_message: string) => "Тип поля определить не удалось. Нажмите кнопку ниже ещё раз.",
    computedAt: (when: string) => `посчитано ${when}`,
    window: (years: number, until: string) =>
      `окно ${years} ${plural(years, "год", "года", "лет")} по ${until}`,
    // Ответ, а не название вопроса: это заголовок первого блока колонки. «Преимущественно»
    // стоит в строке сервера под заголовком, поэтому в самом заголовке оно не повторяется.
    codes: {
      PREDOMINANTLY_ARABLE: "Пашня",
      PREDOMINANTLY_PERENNIAL_GRASS: "Луг",
      // Не «И пашут, и косят»: работу техники мы не видели ни разу, ячейка 1 меряет длину
      // сезонов растительности.
      MIXED_FIELD_USE: "Пашня и луг",
      // «Скорее», а не «Возможно»: словом «возможно» карточка ответа говорит про сход
      // растительности, и две разные шкалы одним словом читались бы как одна.
      POSSIBLY_ARABLE: "Скорее пашня",
      POSSIBLY_PERENNIAL_GRASS: "Скорее луг",
      UNCERTAIN: "Не определено",
    },
    // ЯРУС 3.
    numbers: {
      arableSeasons: "Пахотных сезонов",
      arableDays: "дн.",
      grasslikePeriods: "Травяных периодов",
      grasslikeDays: "дн.",
      secondaryPeriods: "Вторичной вегетации",
      secondaryDays: "дн.",
      peakCandidates: "Кандидатных пиков",
      rejectedPeaks: "Отклонено по длительности",
    },
    intervals: {
      title: "Интервалы вегетации",
      none: "Интервалов активной вегетации не найдено.",
      count: (n: number) => `${n} ${plural(n, "интервал", "интервала", "интервалов")}`,
      duration: (n: number) => `${days(n)} активно`,
      peaks: (n: number) => `${n} ${plural(n, "пик", "пика", "пиков")}`,
      classes: {
        ARABLE_CROP_SEASON: "пахотный сезон",
        GRASSLIKE_LONG_VEGETATION: "травяная вегетация",
        SECONDARY_VEGETATION: "вторичная вегетация",
        ONGOING_CROP_CANDIDATE: "сезон не закончен",
        UNCERTAIN_PEAK_INTERVAL: "пик неуверенный",
        SHORT_ACTIVE_EPISODE: "короткий эпизод",
      },
    },
  },

  // Второй блок колонки — ячейка 2, состояние за 60 дней. Своего заголовка у блока нет с
  // 2026-09-04 (UX_SPEC §3.1): он был третьим голосом о поле. Полная фраза сервера живёт в
  // раскрытии яруса 2, а над карточкой ответа стоит ОДНА строка хода — `dynamics`.
  vegetation: {
    // «Состояние не определено» УДАЛЕНО тем же изменением: это был запасной заголовок блока ②
    // на незнакомый класс сервера, а заголовка у блока нет. Незнакомый класс теперь не даёт
    // строки хода вовсе — и это честнее подписи «не определено» под пустым местом.
    // ХОД РАСТИТЕЛЬНОСТИ, одна строка над карточкой (замечание агронома: карточка отвечает,
    // что с полем сделали, и не отвечает, что на нём происходит). Ключуется `trend`, который
    // и раньше считался из класса состояния, — то есть новых веток решения не заведено ни
    // одной, названы словами те же четыре, что уже красили блок цветом.
    //
    // ЧЕГО ЗДЕСЬ НЕ БЫВАЕТ. Подлежащее только «растительность»: строка сообщает о ней и ни о
    // чём другом. Слова заголовков карточки — уборка, пахота, обработана, посеяна, пары,
    // стерня, «копать» — запрещены поимённо и проверкой: строка стоит НАД заголовком, и
    // второе имя события в ней читалось бы спором с ним. Советов нет: что делать, говорит
    // карточка, а ход растительности — это наблюдение.
    //
    // Дата обязательна у каждой (§3.3): без неё «растёт» читается как «растёт сегодня», а
    // между снимком и сегодня бывает три недели облаков.
    dynamics: {
      rising: (date: string) => `Растительность растёт — по снимку ${date}.`,
      // Не «усыхает» и не «выгорает»: причину убыли снимок не показывает, а «убывает» уже
      // принято словарём (было в классах состояния, ушедших отсюда тем же изменением).
      falling: (date: string) => `Растительность убывает — по снимку ${date}.`,
      flat: (date: string) => `Растительность не меняется — по снимку ${date}.`,
      // `bareFlat` УДАЛЁН 2026-10-09 (сверка словаря состояний). Он говорил «Растительности на
      // поле нет» — то есть называл зону «открытая почва» ЧЕТВЁРТЫМ словом, после полоски
      // уровня, заголовка и строки доказательства, и повторял тот же датированный факт, что
      // строка доказательства под карточкой: «На снимке 22 июля — открытая почва».
      //
      // Про ход в этой зоне сказать нечего: движения нет, а состояние называет полоска. Строки
      // там теперь нет вовсе — у пустого слота меньше голоса, чем у четвёртой копии факта.
      // Правило для рендера: зона «открытая почва» + ровный ход — строка хода не печатается
      // (`flat` туда не подставлять, он утверждал бы, что что-то стоит и держится).
    },
    // ДАТИРОВАННОЕ наблюдение — то, чем блок доказывает заголовок карточки. «Сейчас на поле»
    // здесь стояло без единой даты и обещало свежесть, которой у снимка может не быть: между
    // ним и сегодня бывает три недели облаков (UX_SPEC §3.3). Второй аргумент — имя зоны из
    // `strip.bands`: набор слов на одно множество снимков один на весь продукт.
    observation: (date: string, label: string) => `На снимке ${date} — ${label}.`,
    // Максимум за 60 дней. Число видно только в режиме «Числа», поэтому ключ ОДИН, а веток
    // две: без числа фраза остаётся предложением, а не обрубком с повисшим тире.
    // БЕЗ слова «пик» (peak-note-max): «Пик растительности был 2 июля» стоял строкой под
    // квалификатором «до пика растительности», когда максимум окна пришёлся на последний
    // снимок, — «до пика» и «пик был» в одном блоке. Слово «пик» остаётся у позиций
    // (`positions`), которые говорят о ходе; эта строка говорит о наибольшем измерении окна.
    peakNote: (value: string | null, date: string) =>
      value === null
        ? `Больше всего растительности было ${date}.`
        : `Больше всего растительности было ${date} — NDVI ${value}.`,
    noAcquisitions: NO_ACQUISITIONS_60D,
    stateMissing: "Состояние поля определить не удалось.",
    staleWarning: "Снимки слишком старые: текущее состояние по ним не определить.",
    // `rapid_removal_supported` в решение не входит — поэтому и формулировка без слова
    // «подтверждено»: оно из словаря ответа.
    removalSupported:
      "Тот же спад растительности виден и по другим признакам: влажность растительности " +
      "падает, открытой почвы на снимках всё больше.",
    // Подпись называет СОДЕРЖИМОЕ, а не уровень читателя (правила текстов — ТЗ.md).
    numbersTitle: "Откуда мы это знаем",
    numbers: {
      // «Сейчас» у измерения запрещено: между последним чистым снимком и сегодня бывает три
      // недели облаков, подпись называет, к чему число привязано (UX_SPEC §3.3).
      ndviNow: "NDVI на последнем чистом снимке",
      change30: "изменение NDVI за 30 дней",
      peakDate: "пик растительности",
      dropFromPeak: "спад от пика",
    },
    // `confidenceLevels` и `confidenceInline` УДАЛЕНЫ 2026-09-05 (решение владельца,
    // (правила текстов — ТЗ.md): «уверенность измерения» — внутренняя шкала, продукт её не
    // носит ни на одном ярусе.
    // ЯРУС 3. NDMI_FALLING держит ту же формулу, что `removalSupported`: «покров» — второй
    // синоним на то же понятие, дубль-с-расхождением (removal-supported).
    supportFlags: {
      NDMI_FALLING: "влажность растительности падает (NDMI)",
      BSI_RISING: "растёт индекс открытой почвы (BSI)",
    },
    // Шестнадцать классов направления (`classify_direction_60d`) отсюда УДАЛЕНЫ 2026-09-05.
    // Они были заголовком блока ②, заголовок сняли как третий голос о поле (UX_SPEC §3.1), и
    // с того дня их не печатало ничто: увидеть это мешало ровно исключение в `DYNAMIC_GROUPS`,
    // которое и написано для словарей, ключуемых сервером. Шестнадцать формулировок под
    // защитой от собственного сторожа — это хуже, чем ни одной: они выглядели поддерживаемыми.
    //
    // Заменены не один-в-один, а сознательно грубее: `dynamics` из четырёх строк по `trend`.
    // Различия вроде «убывает с высокого уровня» против «быстро убывает» ни на одно решение
    // копателя не влияли, а на экране спорили с заголовком карточки. Точный класс никуда не
    // делся: сервер присылает свою фразу целиком (`sentence`), и она стоит в раскрытии
    // «Откуда мы это знаем» — то есть на ярусе 2, где ей и место.
    // Положение относительно пика, classify_local_position_60d. «Локальный максимум» —
    // рабочее слово чужой профессии. «Пик» всегда полностью — «пик растительности» — во всех
    // пяти позициях: голый «пик» в приписке читался без предмета (vegetation-positions).
    positions: {
      BEFORE_LOCAL_MAXIMUM: "до пика растительности",
      AT_LOCAL_MAXIMUM_OR_PLATEAU: "растительность на пике",
      AFTER_LOCAL_MAXIMUM: "пик растительности пройден",
      NO_CLEAR_LOCAL_POSITION: "пик растительности не выражен",
      UNKNOWN: "пик растительности не определён",
    },
  },

  // ───────────────────────────────────────────────────────────────────────────────────────
  // КАРТОЧКА ОТВЕТА. ШЕСТЬ заголовков владельца (решение 2026-09-04, (правила текстов — ТЗ.md)
  // §14, §14.9) на шесть внутренних состояний: заголовок выбирает не одно состояние, а ПАРА
  // «состояние × тип поля», а у схода растительности ещё и зона последнего чистого снимка.
  // На лугу стоящая трава и сход дают ОДИН заголовок — укос не меняет того, что поле
  // остаётся лугом.
  //
  // Заголовки утверждают больше, чем измерено: детектор ловит сход растительности, а уборку,
  // укос, потраву и высыхание не различает. Работу техники он не видит вовсе — и с §14.9 это
  // относится уже и к №3: пахота там прочитана по УРОВНЮ растительности на последнем снимке,
  // а не по виду земли. Владелец принял это сознательно, и цена названа поимённо: под каждым
  // таким заголовком ВСЕГДА стоит оговорка `caveat.*`. Не в раскрытии — раскрытие можно не
  // открыть, а заголовок прочитан уже всеми.
  // ───────────────────────────────────────────────────────────────────────────────────────
  verdict: {
    heading: {
      // №1 — crop × пашня / скорее пашня / пашня и луг / тип не посчитан. «Культура» здесь
      // не наша скоропись, а слово владельца: до 2026-09-04 словарь его запрещал.
      cropSown: "На поле посеяна культура (копать запрещено)",
      // №2 — сход растительности при тех же типах поля. ЕДИНСТВЕННОЕ место продукта, которое
      // утверждает уборку.
      //
      // Хвост «— на земле стерня» печатается РОВНО на зоне «стерня», и ни на какой другой:
      // он утверждает, что осталось на земле, и без той самой зоны это утверждение ничем не
      // подпёрто. На всех остальных зонах, включая непрочитанную, стоит `harvestedNoSurface`
      // без хвоста. `kind` на выбор не влияет — зона либо прочитана, либо нет (правка «г»
      // 2026-09-04: прежний порядок приравнивал к «зоны нет» целые ветки ответа, и комментарий
      // здесь пережил его на месяц).
      //
      // О самой поверхности говорит `surfaceUnknown`, и только там, где её не прочитали, а тон
      // осторожный: при прочитанной зоне о ней говорит полоска уровня, и второй голос был бы
      // спором. `caveat.arable` / `caveat.unknownUse` обязательны под обоими заголовками: без
      // них заголовок утверждал бы событие, которого мы не наблюдали.
      harvested: "Поле убрано — на земле стерня",
      harvestedNoSurface: "Поле убрано",
      // №3 — тот же сход растительности и те же типы поля, что у №2, но последний чистый
      // снимок попал в зону «открытая почва», а не «стерня». Разделяет №2 и №3 ОДНА
      // величина — сколько растительности осталось на поле; вида земли снимок не показывает
      // ни в одном канале, и «пахота» здесь прочитана по уровню, а не по борозде. Оговорка
      // `caveat.tilled` под ним обязательна и именно это и говорит.
      // Объект «почва» обязателен: без него читалась химобработка (headings-harvested-tilled).
      tilled: "Почва обработана — пахота или дискование",
      // №4 — луг / скорее луг при любом состоянии, кроме пара и отсутствия снимков. Один
      // заголовок на два состояния: и когда трава стоит, и когда её скосили.
      perennialGrass: "Поле под многолетними травами (копать запрещено)",
      // №5 — пар, при ЛЮБОМ типе поля: чистый пар выглядит одинаково и на пашне, и на лугу.
      notSown: "Поле не сеяно (пары)",
      // №6 — единственный заголовок, который говорит о СЪЁМКЕ, а не о поле. Причина
      // печатается строкой ниже (`reason.*`) и обязательна: без неё заголовок не отвечает,
      // чего ждать и сколько.
      // «Чистый» — единственное слово продукта о пригодном снимке; «чёткий» не существует.
      noShots: "Нет чистых снимков спутника",
      // №6б — то же состояние «ответа нет», но снимки БЫЛИ: фолбэк `reason.noEvent`
      // срабатывает и при дюжине чистых снимков (lib/verdict.ts), и «Нет чистых снимков
      // спутника» стояло ровно над причиной «Чистые снимки есть…» — две соседние строки
      // спорили на каждом HARVEST_NOT_CONFIRMED (heading-noshots-clean). Выбирается ПО
      // ПРИЧИНЕ, а не по состоянию: `noDataHeading` в lib/verdict.ts.
      // «Однозначного», не «Определённого»: бюджет слова заголовка на узкой консоли —
      // 12 знаков (styles/visual.test.ts).
      noAnswer: "Однозначного ответа нет",
    },

    // ─── Тезис: одна фраза о ПОЛЕ ────────────────────────────────────────────────────────
    // Глагол один на все датированные состояния — «сошла». Продукт ловит сход растительности,
    // а не уборку.
    eventBetween: (span: string) => `Растительность сошла между ${span}.`,
    eventSingle: (day: string) => `Растительность сошла ${day}.`,
    // Один корень «грубо/грубее» на всех поверхностях датировки; «приблизительная» не
    // употребляется (event-by-radar, глоссарий).
    eventByRadar: (span: string) => `Растительность сошла между ${span}; день известен грубо.`,
    eventByRadarSingle: (day: string) => `Растительность сошла ${day}; день известен грубо.`,
    // Приписка к факту НА ЛУГУ. Заголовок №4 там один и тот же у стоящей травы и у схода,
    // поэтому сказать, что именно произошло, обязан факт. Без «а не уборка»: слово «уборка»
    // на луговой карточке читалось бы подсказкой «поле пахотное» (grass-tail).
    eventGrassTail: "На лугу это укос.",
    // Полное предложение с подлежащим: голая «Дата не определена» читалась без предмета —
    // чья дата и дата чего (закон зачистки 2026-09-05).
    eventUnknown: "Растительность сошла, а день по снимкам не определён.",
    canopyGone: "Растительность на поле была и сошла.",
    // С ДАТОЙ: «было вчера» и «было 40 дней назад» — разница между «не езжай» и «мы не
    // знаем». Числа NDVI в строке нет, оно живёт в раскрытии.
    cropOn: (date: string) => `На снимке ${date} поле было под растительностью.`,
    cropPlain: "На последних снимках поле было под растительностью.",
    fallowPlain: "За весь сезон растительность на поле не появилась.",
    // ПОВЕРХНОСТЬ НЕ ПРОЧИТАНА — единственная строка карточки о том, что на земле, и стоит она
    // только там, где полоски уровня на экране нет вовсе. Тон уборки берётся от поверхности
    // (стерня — не открытая почва, и по ней не копают), поэтому жёлтый без зоны обязан быть
    // объяснён словами; когда зона есть, её называет полоска, и второй раз — уже спор.
    surfaceUnknown: "Что сейчас на земле — стерня или открытая почва, — по снимкам не видно.",

    // ─── Характер схода: живёт в раскрытии «Что видно на снимках», НЕ на карточке ─────────
    // Балл 0–100 ушёл с продуктовой поверхности целиком (живёт в `dev.score`), а с 2026-09-05
    // с карточки ушли и эти две строки (drop-lines-off-card). Глагол события один — «сошла/
    // сходила»; «исчезла» и «спад» без подлежащего «растительность» не печатаются. Ключи
    // `changeNotApplicable` / `changeUnknownMaybe` / `changeUnknownRadar` /
    // `changeUnknownNoData` УДАЛЕНЫ: тройной дубль темы «даты нет» (осталась одна строка —
    // `canopyGoneNoDate`), дубль `radarLine` об облаках и мёртвая ветка nodata.
    dropSharp: "Растительность сошла резко — за один интервал между съёмками.",
    dropGradual: "Растительность сходила постепенно, от снимка к снимку.",

    // ─── Строка 4: то, что делает тезис утверждением ─────────────────────────────────────
    // «Сухое состояние» — наше слово, и «сухой» рядом с полем читается как проходимость,
    // которую мы не меряем вовсе. Сказано ровно наблюдение.
    // ДВЕ даты, и вторая обязательна: «прошло 27 дней» читалось как «к сегодняшнему дню», а
    // счёт идёт ко дню, на который считали, и его на карточке не было вовсе. Анализ недельной
    // давности этим враньём удлинял срок ровно на свой возраст (dry-anchor). Теперь срок
    // закрыт с обеих сторон, и вторая дата названа тем, что она есть, — словом «анализ»,
    // единственным именем действия (§15.1); «день расчёта» был бы вторым.
    dryConfirmed: (since: string, until: string, n: number) =>
      `Растительность не возвращалась ${days(n)}: с ${since} по ${until}, день анализа.`,
    dryNotConfirmed: "Прошло мало времени — растительность ещё может вернуться.",
    // ОДНА фраза вместо трёх копий, и она связывает оптику с радаром ВО ВРЕМЕНИ. Совет
    // называет орган точными словами кнопки «Показать снимок»; «осмотр» — слово выезда, а не
    // экрана (radar-line-tail). В `radarLineNoDate` чистых дат нет и кнопки может не быть —
    // совет там не печатается вовсе: он называл бы отсутствующий орган.
    radarLine: (opticalDate: string) =>
      `Оптические снимки закрыты облаками: растительность они видели до ${opticalDate}. ` +
      `Дальше сработал радар — он видит сквозь облака, но дату определяет грубее. ` +
      `Посмотрите снимки сами — кнопка «Показать снимок» ниже.`,
    radarLineNoDate:
      "Оптических снимков не хватило. Сработал радар — он видит сквозь облака, но дату " +
      "определяет грубее.",
    canopyGoneNoDate:
      "Когда именно это случилось, по снимкам автоматически не определить. " +
      "Посмотрите снимки сами — кнопка «Показать снимок» ниже.",
    // «Мы» осознанно: незнание наше, а не свойство даты.
    cropUnknownAfter: "Что было после этой даты, мы не знаем.",
    fallowSpan: (dates: number, from: string, to: string) =>
      `${dates} ${plural(dates, "чистый снимок", "чистых снимка", "чистых снимков")} ` +
      `с ${from} по ${to} — растительности ни на одном.`,
    // Приглашение вернуться — только у шестого состояния, и оно разное по причине: снег,
    // сплошная облачность и «съёмок не было» ждут разного и разных сроков. «Посмотрим» обещало
    // пересмотр, которого продукт не делает; орган назван точными словами кнопки
    // (`run.startAgain`, comeback-family; расхождение с «мы»-списком §11.3 —
    // (правила текстов — ТЗ.md).
    comeBack: "Когда появятся чистые снимки, запустите «Анализ заново».",
    comeBackSnow: "Когда сойдёт снег, запустите «Анализ заново».",
    comeBackCloud: "После первого ясного дня запустите «Анализ заново».",
    comeBackNoAcquisitions:
      "Съёмок над этим полем не было — запустите «Анализ заново» через неделю.",
    // Пара к заголовку «Однозначного ответа нет»: чистые снимки БЫЛИ, и «когда появятся
    // чистые снимки» обещало бы ждать того, что уже есть (heading-noshots-clean).
    comeBackNoAnswer:
      "Новые съёмки могут дать ответ — запустите «Анализ заново» через несколько дней.",

    // ─── Оговорка: цена заголовка, и она видна ВСЕГДА ────────────────────────────────────
    // До 2026-09-04 этот слот назывался трактовкой и отвечал на «что из этого следует» при
    // заголовке, который говорил только про измеренное («растительность сошла»). Теперь
    // трактовка переехала В САМ ЗАГОЛОВОК, а слот держит то единственное, чем заголовок
    // снят: перечисление причин, между которыми снимок не выбирает. Правило прежнее —
    // причин НЕСКОЛЬКО и они названы неразличимыми; условная форма («если это была уборка»)
    // ушла, потому что условность стала бы спором с заголовком, стоящим строкой выше.
    // Частотных слов («чаще всего») нет: долей причин мы не мерили ни разу.
    // У «Нет чистых снимков спутника» оговорки НЕТ — там нечего оговаривать.
    caveat: {
      prefix: "Что это значит",
      // Пашня, зона «стерня» (заголовок №2). Четыре неразличимые причины — и граница с №3:
      // второе предложение прежде говорило «вспахать могли, на снимках это не видно», а с
      // 2026-09-04 у пахоты есть свой заголовок, и та формулировка спорила бы с ним через
      // строку. Сказано то, что осталось верным: на ЭТОМ снимке земля ещё под стернёй, а
      // работу техники после него не видно по-прежнему.
      arable:
        "Уборку, укос, высыхание и потраву по снимкам не различить. На последнем снимке поле " +
        "под стернёй: вспахали его позже или нет — по снимкам не сказать.",
      // Пашня, зона «открытая почва» (заголовок №3). Оговорка тяжелее прочих, потому что и
      // заголовок над ней самый смелый: он называет работу техники, которой ни один канал не
      // видит. Первое предложение общее со стернёй — растительность всё равно сошла неизвестно
      // от чего; второе снимает сам заголовок, называя, чем на самом деле различены №2 и №3, и
      // ОДНО поле, выглядящее так же; третье говорит, чем проверить.
      tilled:
        "Уборку, укос, высыхание и потраву по снимкам не различить. " +
        TILLED_BASIS +
        // «На месте» — закреплённое слово выезда; «осмотром» уходит (caveat-tilled).
        " Убедиться можно только на месте.",
      // Луг: уборки в перечислении нет — на лугу её не бывает, и лишнее слово читалось бы
      // как подсказка «поле пахотное».
      grass: "Укос, потраву и высыхание по снимкам не различить.",
      // ТРАВЯНОЕ ПОЛЕ, КОТОРОЕ ЗАДИСКОВАЛИ (2026-10-09, по жалобе на «Облачник»). Заголовок
      // здесь идёт по зоне — №3, «Почва обработана», — хотя три года поле читалось травой, и
      // оговорка снимает ОБА утверждения, которые в этой паре спорят.
      //
      // Порядок такой, потому что таков порядок вопросов у человека: сперва «почему вы годами
      // звали это травой», потом «насколько твёрдо вы говорите про обработку сейчас». Первое
      // предложение — единственное место продукта, где сказано, что `PREDOMINANTLY_PERENNIAL_
      // GRASS` не означает сеяных трав: длинная вегетация без пахотных сезонов одинаково
      // выглядит и у луга, и у заброшки, а копателю это ровно та разница, за которой он едет.
      //
      // `TILLED_BASIS` здесь НЕ подставлен, хотя тема та же: он кончается словами «так же
      // выглядит поле, убранное до земли», а на травяном поле уборки не бывает — это та самая
      // подсказка «поле пахотное», которой избегает `grass`. Вместо неё названы причины,
      // возможные на траве, и это ТА ЖЕ ТРОЙКА, что в `grass` — укос, потрава, высыхание:
      // два голоса об одном поле не должны перечислять разное.
      //
      // Основание заголовка (чем пахота отделена от прочего) сказано СЛЕДСТВИЕМ и в той же
      // фразе, что неразличимость, а не отдельным предложением: «снимок меряет, сколько
      // осталось растительности, а не как выглядит земля» — это причина, по которой различить
      // нельзя, и читается она как объяснение, а не как четвёртое утверждение. Слово «стерня»
      // из `TILLED_BASIS` сюда не переносится: на травяном поле стерни не бывает, и
      // альтернативой пахоте там выступает укос, а не жнивьё.
      grassTilled:
        "Годами это поле стояло в траве: сеяные травы от заброшенного по снимкам не отличить. " +
        "Обработку почвы от укоса, потравы и высыхания тоже не различить: снимок меряет, " +
        "сколько осталось растительности, а не как выглядит земля. Убедиться можно только " +
        "на месте.",
      // Тип поля не посчитан, а заголовок всё равно назвал работу на пашне. Второе
      // предложение поправляет ЗАГОЛОВОК, а не поле: оно единственное место, где сказано, что
      // №2 или №3 мог оказаться №4. Хвост отрицает ОБА заголовка, под которыми эта оговорка
      // стоит: «ни уборки» — про №2, «ни пахоты» — про №3. Прежний хвост «скосили, а не
      // убрали и не запахали» — три глагола с двумя отрицаниями — читался со второго прохода
      // (caveat-unknownuse-tail): теперь утверждение одно («скосили»), а оба отрицания
      // собраны в одно слышное перечисление «ни… ни…».
      unknownUse: "Уборку, укос, высыхание и потраву по снимкам не различить. " + UNKNOWN_USE_TAIL,
      // Та же пара «тип не посчитан», но под заголовком №3, и порядок здесь ОБРАТНЫЙ по
      // необходимости. Общая оговорка начинается с перечисления неразличимых причин, и под
      // «Почва обработана» это читалось прямым спором: заголовок утверждает пахоту, а строка
      // под ним первым же словом принимается перечислять, чего мы не знаем. Поэтому сначала
      // поправлено ОСНОВАНИЕ заголовка (чем пахота прочитана), и только потом тип поля —
      // читатель сперва узнаёт, насколько твёрдо сказанное над строкой, а потом что оно вообще
      // могло быть о другом поле. Строки «убедиться можно только на месте» здесь нет: слот
      // один, а поправка о типе тяжелее — она меняет не точность заголовка, а сам заголовок.
      unknownUseTilled: TILLED_BASIS + " " + UNKNOWN_USE_TAIL,
      // Приписка у состояния без даты — тема СРОКА, не даты: датой занимается
      // `canopyGoneNoDate`. «Даты у схода нет» читалось про сельский сход (caveat-no-date).
      noDate: "Сколько времени поле стоит без растительности, по снимкам не сказать.",
      // «Поле не сеяно (пары)» — единственная оговорка, которая называет не причину схода, а
      // два других поля, выглядящих так же. Стояла в раскрытии «Что видно на снимках»; под
      // заголовком, который утверждает пар, раскрытию её доверять нельзя.
      // «И… и…» делает перечисление слышным с первого слова (caveat-fallow).
      fallow:
        "Так же выглядят и поле, засеянное позже последнего снимка, и редкие всходы, " +
        "которые не закрыли почву.",
    },

    // ─── Ярус 2: что видно на снимках ────────────────────────────────────────────────────
    // `worthTitle` и `worth` УДАЛЕНЫ 2026-09-05 (закон зачистки, (правила текстов — ТЗ.md):
    // продукт о своей точности не говорит нигде, ни на каком ярусе — счёт попаданий,
    // выборки и валидации живут в docs и в git-истории. Единственное допустимое эхо —
    // «убедиться можно только на месте» там, где совет осмотра уместен (caveat.tilled,
    // cropCaption).
    // ЧТО БЫЛО ВИДНО, а не по какому признаку решено: канал, поведение канала и величину
    // пика до события печатать нельзя — это само правило (правила текстов — ТЗ.md).
    measuredTitle: "Что видно на снимках",
    measuredEvent:
      "На снимках до этой даты поле было под растительностью, на следующих — уже нет. " +
      "Почему растительность сошла, по снимкам не видно.",
    // О ПОЛЕ, а не о нашей проверке: отчёт «проверенных полей у нас нет ни одного» ушёл в
    // docs (§16). Осталась оговорка про само поле — «убран урожай или нет» решает осмотр,
    // снимок видит только уровень растительности.
    cropCaption:
      "Это наблюдение по уровню растительности на снимках: убран урожай или нет, " +
      "окончательно покажет только осмотр на месте.",
    // `fallowCaption` УДАЛЁН (2026-09-04): перечисление режимов отказа переехало в
    // `caveat.fallow` и печатается на экране всегда. В раскрытии оно стояло под заголовком,
    // который ещё не утверждал пар; заголовок №5 утверждает, и оговорка не может ждать клика.
    // `fallowEvidence` УДАЛЁН 2026-09-05 (закон зачистки, §16): «с поправкой на случайность»
    // пересказывал границу Уилсона — внутреннюю методику, которой продукт не носит. Счёт
    // чистых снимков окна печатает `fallowSpan`, и его довод полю достаточен.

    // ─── Остальное ───────────────────────────────────────────────────────────────────────
    // «Ответ появится после расчёта» УДАЛЕНО вместе с блоком, который его печатал: пустой
    // блок с обещанием — это и есть та пустота, ради которой правило §12.1 написано. Блока
    // без содержимого в DOM нет, а состояние прогона держит кнопка (`run.busy`).
    // Состояние, которое велит посмотреть снимок, обязано давать способ это сделать.
    showImage: (date: string) => `Показать снимок ${date}`,
    // Счёта снимков здесь БОЛЬШЕ НЕТ (2026-09-04, UX_SPEC §3.5): знаменатель на экране один,
    // и печатает его блок снимков (`strip.summary`). Карточка отвечает за поле, а два счёта
    // одного множества в одной колонке читались как спор — тем более что считали они разное.
    // ЯРУС 3.
    warnings: "Предупреждения прогона",
  },

  // Причина, по которой ответа нет. У шестого состояния причина И ЕСТЬ тезис.
  reason: {
    // «Отклонено» — наше слово, и на продуктовой поверхности его нет ни в одном падеже:
    // снимок не «отклонён», он «не прошёл проверку». Сколько их было и за какой срок,
    // печатает блок снимков: у окна один знаменатель на всю колонку.
    noAcceptedDates: NO_CLEAR_DATES,
    clouds: (percent: number) => `Облака и тени закрывали в среднем ${percent} % площади.`,
    stale: (n: number) => `Чистых снимков нет ${days(n)}.`,
    snow: (percent: number) => `Поле под снегом: закрыто в среднем ${percent} % площади.`,
    tooSmall: TOO_SMALL,
    // Последняя ветка причины — ФОЛБЭК: срабатывает и при 12 чистых снимках (verdict.ts:378),
    // поэтому «их слишком мало» приписывал причину, а «что случилось» пресуппозировал событие.
    // «Ничего определённого на них не видно» (noevent-text) спорило с блоком доказательства,
    // который называет, ЧТО на снимке видно («На снимке 28 июля — открытая почва»): не видно
    // не снимок, а ОТВЕТ по полю (noevent-text-2). Заголовок над этой причиной — свой,
    // `verdict.heading.noAnswer`: «Нет чистых снимков» строкой выше был бы прямым спором.
    noEvent: "Чистые снимки есть, но что с полем, по ним не определить.",
    // «Не записана» отчитывалось о нашем журнале — внутренней кухне, которой продукт не
    // носит (закон зачистки 2026-09-05). Сказано ровно то, что знает читатель.
    unknown: "Причина неизвестна.",
    // Отказ адресуется по КОДУ, а не пересказом английского сообщения сервера: код приходит
    // в `error_reason`, а английский текст живёт на ярусе 3 (`dev.runError`) целиком.
    byCode: (code: string | null | undefined): string =>
      ({
        no_acquisitions: "Съёмок над этим полем не было.",
        no_clear_dates: NO_CLEAR_DATES,
        // QA считает облако и тень вместе (`filterCleanWhat`) — один код, один объём.
        cloudy: "Все снимки закрыты облаками и тенью.",
        field_too_large:
          "Поле слишком большое — анализ по нему не запускается. Обведите участок поменьше.",
        field_too_small: TOO_SMALL,
        invalid_geometry: "Контур не годится: линии пересекаются или точек меньше трёх.",
        // «Попробуйте позже» без предмета запрещено — орган назван словами кнопки
        // «Повторить анализ» (reason-retry-later, reason-restarted, reason-internal).
        gee_unavailable: "Спутниковая служба не отвечает. Повторите анализ позже.",
        database_unavailable: "Сбой на сервере. Повторите анализ позже.",
        service_restarted: "Сервер перезапустился — нажмите «Повторить анализ».",
        // Без «мы её записали»: аудит — наша кухня, читателю нужен виновник и орган
        // (закон зачистки 2026-09-05).
        internal_error: "Ошибка на нашей стороне. Нажмите «Повторить анализ».",
        unauthorized: "Сессия истекла — войдите заново.",
        // Та же формулировка, что у `run.failed`: один отказ не должен звучать двумя фразами.
      })[code ?? ""] ?? "Анализ не удался.",
  },

  // ЯРУС 3. Канал оговорок прогона; кодов сейчас нет ни одного, рендер остаётся.
  notes: {
    unknown: (code: string) => `Оговорка прогона, код не распознан: ${code}`,
  },

  // ОДИН НАБОР СЛОВ на одно множество снимков, для полосы состояния и для календаря сразу.
  // Пока их было два, легенда полосы говорила «снимок чистый», легенда календаря — «чистый
  // снимок», а строка для скринридера — «чистый»: три имени одного множества, из-за которых
  // счётчики читались как спор.
  qa: {
    clean: "чистый снимок",
    rejected: "не прошёл проверку",
    unscored: "без проверки",
    // Единственная причина штриховки: сомнение в ИЗМЕРЕНИИ, не в классе.
    uncertain: "измерение неуверенное",
  },

  strip: {
    title: "Полоса состояния",
    // Что график утверждает — до того, как на него смотрят. Здесь же ЕДИНСТВЕННАЯ на весь
    // продукт расшифровка NDVI. Числовых опор в ней нет: 0.65 — порог правила, а не подпись
    // к картинке; верх и низ названы теми же словами, что и зоны оси.
    // «Столбик — съёмка» согласован со сводкой «24 съёмки» и `columnAria` (strip-reading).
    reading:
      "Каждый столбик — одна съёмка; высота — NDVI, то есть сколько на поле растительности. " +
      "Внизу открытая почва, вверху густая растительность.",
    // Подписи ЗОН оси значений: класс читается по положению колонки, без наведения и без
    // сверки с цветом. «Стерня» остаётся — обычное точное слово, «сухая трава» расплывчатее.
    bands: {
      vegetation: "растительность",
      stubble: "стерня",
      bare: "открытая почва",
    },
    // «Шире = грубее», по-людски «хуже известен»; корень «груб-» остаётся у `radarLine` и
    // `eventByRadar` (legend-bracket).
    legendBracket: "когда сошла растительность; чем шире скоба, тем хуже известен день",
    legendStale: "свежих снимков нет",
    empty: NO_ACQUISITIONS_60D,
    columnAria: (date: string, state: string) => `Съёмка ${date}: ${state}`,
    uncertainNoNdvi: "NDVI не посчитан",
    // `undated` УДАЛЁН 2026-09-05 (закон зачистки): «съёмка без даты» — отчёт о нашем
    // конвейере, не о поле. Учёт таких съёмок — журналу яруса 3, не графику.
    // Строка-показание под полосой: сравнение дат стрелками, без мыши.
    selected: (date: string, state: string) => `${date} — ${state}`,
    // Имя величины у самой оси. Ровно четыре знака — столько же, сколько у «0.25» под ним:
    // в левом поле оси помещается только то, что не длиннее числовой отметки. Расшифровка
    // живёт строкой над графиком (`reading`), где текст умеет переноситься.
    scale: "NDVI",
    // «День анализа» — дословно слово карточки (`dryConfirmed`): одна дата, одно имя.
    today: "день анализа",
    // ОДНО предложение на весь блок снимков — и ЕДИНСТВЕННЫЙ знаменатель на экране
    // (UX_SPEC §3.5, §11.5). До 2026-09-04 то же множество считали четыре ключа в трёх
    // блоках: заголовок раскрытия со своим ключом, подпись оси, строка QA у карточки и
    // причина отказа. «Последняя» стоит С ПРИЛАГАТЕЛЬНЫМ всегда: последняя съёмка и
    // последняя чистая — разные даты, и без прилагательного они читались как одна.
    summary: (total: number, clean: number, lastCleanDate: string | null) =>
      lastCleanDate === null
        ? `${shots(total)} за 60 дней · чистых ${clean}`
        : `${shots(total)} за 60 дней · чистых ${clean} · последняя чистая ${lastCleanDate}`,
    // Внутри раскрытия, у самого графика: у него свой счёт — даты, на которых NDVI вообще
    // посчитан. Он меньше числа съёмок, и без подписи два числа читались как спор.
    graphNote: (n: number) => `на графике ${dates(n)} с измеримым NDVI`,
    // ЯРУС 3: подписи числовой всплывашки колонки.
    ndvi: "NDVI",
    ndmi: "NDMI",
    bsi: "BSI",
    snow: "снег",
    valid: "пригодных пикселей",
    bad: "облако/тень",
  },

  // Календарь съёмок над видимой областью. Множество называет прямо: календарь считает
  // пролёты над ЭКРАНОМ, полоса состояния — съёмки над ПОЛЕМ.
  calendar: {
    title: "Календарь съёмок",
    open: "Показать календарь",
    close: "Скрыть календарь",
    filterClean: "Только чистые снимки",
    // ЕДИНСТВЕННОЕ определение «чистого» на весь продукт, и честность держит именно оно, а не
    // прилагательное: облако до 10 % проходит, снег не отбраковывается вовсе.
    filterCleanWhat:
      "Чистый снимок — тот, на котором поле попало в кадр, облако и тень заняли не больше " +
      "10 % его площади, а разобрать удалось не меньше 90 % пикселей. Снег при этом не " +
      "мешает: заснеженный снимок тоже бывает чистым.",
    // «i» появляется ровно у отклонённой даты: «принята» и «не оценивалась» — отчёт о нашей
    // работе, на решение ехать он не влияет, а отклонение объясняет, почему снимок выглядит
    // хуже соседних и почему на нём не стоит ответ.
    why: "Почему снимок не прошёл проверку",
    whyRejected: (reason: string) => `Снимок не прошёл проверку. ${reason}.`,
    // Заголовок-предложение. Область и окно стоят В НЁМ, потому что календарь считает пролёты
    // над ЭКРАНОМ, а не над полем: слова «поле» он не произносит ни разу (UX_SPEC §11.5).
    // Прежде это были три узла — «Чистых 12 из 24», «над видимой областью» и месяц над
    // сеткой, — и первый читался как счёт по выбранному полю.
    heading: (window: string | null, clean: number, total: number) =>
      window === null
        ? `Над видимой областью карты: чистых ${clean} из ${total}`
        : `Над видимой областью карты, ${window}: чистых ${clean} из ${total}`,
    counts: (accepted: number, rejected: number, unscored: number) =>
      `чистых ${accepted} · не прошли ${rejected} · без проверки ${unscored}`,
    empty: "Дат пока нет: приблизьте карту или запустите анализ поля.",
    ariaDay: (date: string, state: string, reason: string | null) =>
      reason ? `${date}: ${state}, ${reason}` : `${date}: ${state}`,
    outsideWindow: "не входит в анализ",
  },

  // Подложка и снимок на дату. Подложка включена всегда, снимок — переключатель.
  imagery: {
    basemapLabel: "Подложка",
    basemapHybrid: "Снимок",
    basemapStreet: "Карта",
    // Имя органа целиком: живёт в `aria-label` группы, на экране его нет.
    title: "Снимок на дату",
    // Номер зума убран: счётчика зума на экране нет, проверить «нужен 14 и ближе» нечем.
    zoomHint: "Приблизьте карту, чтобы искать снимки.",
    loadingDates: "Поиск дат…",
    noDates: "За год над этой областью нет съёмок без облаков.",
    // Без «попробуйте позже»: органа перезагрузки дат у экрана нет — совет вёл бы в тупик.
    datesFailed: "Даты съёмок не загрузились.",
    // ДОСТУПНОЕ ИМЯ ИКОНКИ, а не подпись на ней: слова живут в `aria-label` и `title`, там
    // же, где глаголы всех остальных иконок над картой.
    show: "Показать снимок",
    hide: "Скрыть снимок",

    // ПЕРЕКЛЮЧАТЕЛЬ СЛОЯ ОТМЕНЁН ВЛАДЕЛЬЦЕМ 2026-10-02, до того как его успели запросить:
    // «никакие переключатели не надо добавлять». Сняты все пять подписей — имя группы, два
    // положения («Снимок» / «Растительность») и края цветовой шкалы.
    //
    // Вырезка слежения из того же решения ОСТАЁТСЯ: кнопка «Следить», её обещание и строки
    // защёлки удалены и не возвращаются. Отменена только добавка.
    //
    // Если слой вернётся, вернутся и слова — вместе с вопросом, который они закрывали:
    // шкала цвета сама себя не объясняет, а единственная расшифровка этой величины живёт под
    // полосой состояния (`strip.reading`), которой на карте нет.
  },

  // Радар. Замер (docs/EXP_RADAR_VALUE.md) говорит, что радарное уточнение НЕ улучшает
  // датировку и измеримо ухудшает попадание интервала в событие, поэтому ни одно слово здесь
  // не должно читаться как «радар подтверждает». «Подтверждено» и «проверено» рядом с радаром
  // запрещены: отложенной проверки у этого пути нет ни одной.
  radar: {
    // Имя прибора (Sentinel-1) уезжает на ярус 3: «радар» знает каждый. Эвфемизм «второй
    // спутник» был бы хуже — он прячет, что прибор другой, и подталкивает читать радарный
    // ответ как подтверждение оптического.
    title: "Радар",
    notice: "Показан для справки, на ответ не влияет.",
    // Заменяет `notice`, когда ответ РАДАРНЫЙ: тогда «на ответ не влияет» было бы ложью.
    decides: "На этих данных и стоит ответ: оптических снимков не хватило.",
    ariaChart: "Ряд VV и VH по датам. Показан для справки, на ответ не влияет.",
    // Пусто — нормальный ответ, а не поломка: над половиной страны Sentinel-1 проходит раз в
    // 12 суток, а восточнее Волги восходящих проходов нет вовсе.
    emptyWindow: "Пригодных снимков радара в окне нет.",
    // Скоба события над радарным рядом — только когда ответ РАДАРНЫЙ: чужой интервал на
    // чужом сенсоре намекал бы на подтверждение, которого замер не даёт.
    eventLabel: (from: string, to: string) => (from === to ? from : `${from} — ${to}`),
    // ЯРУС 3 отсюда и ниже — печатается только под `dev`. `why` и `whyDecides` УДАЛЕНЫ
    // 2026-09-05 (зачистка: методология живёт в docs, не в словаре): числа замера, доли
    // попаданий и слова о качестве пути не печатаются ни на одном ярусе, включая этот.
    numbersTitle: "Числа по радару",
    vv: "VV",
    vh: "VH",
    axisDecibels: "дБ",
    // Лента между VV и VH: её расширение и есть рост RFDI.
    gapTitle: "разность VV − VH",
    window: (from: string, to: string) => `Окно ${from} — ${to}`,
    counts: (accepted: number, seen: number, tracks: number) =>
      `Принято ${accepted} из ${seen} · треков ${tracks}`,
    point: (date: string, vv: string, vh: string, track: string) =>
      `${date}: VV ${vv} дБ, VH ${vh} дБ, трек ${track}`,
  },

  // СЛЕЖЕНИЕ ЗА ПОЛЕМ УДАЛЕНО ЦЕЛИКОМ 2026-10-02, решение владельца: «уберём пока эту фичу
  // про отслеживание, сбивает с толку пока нет подписок». Ушли «Следить» / «Не следить»,
  // доступное имя защёлки и обещание («Когда поле станет голым, это появится здесь»).
  //
  // Обещание и было тем, чего продукт сдержать не мог: доставки у него нет вовсе, и «появится
  // здесь» значило «зайдите и проверьте сами» — то есть ровно то, что человек делает и без
  // защёлки. Пока нет подписок, кнопка просила согласия на услугу, которой не существует.
  //
  // Слово «голое» ушло вместе с ней: на экране его больше нет ни в одном падеже — состояние
  // поля называет карточка ответа своими заголовками.
  //
  // Серверные ручки (`api/watch.py`) живы, UI их не зовёт. Вернуть фичу — значит писать слова
  // заново, и с тем же вопросом, на который прежние ответить не могли: чем именно уведомим.

  // ЯРУС 3. Внесение наблюдения: в продукте — доступная кнопка, слепая форма живёт только в
  // режиме верификации (VERIFICATION_MODE). Пользователь продукта наблюдений не вносит.
  groundTruth: {
    title: "Что было на самом деле",
    open: "Внести наблюдение",
    sealed: "Состояние и ответ запечатаны",
    sealedHint:
      "Сначала запишите, что видели на поле. Тип поля показан нарочно: убрано ли поле сейчас, он не подсказывает.",
    reveal: "Показать вердикт без наблюдения",
    revealHint:
      "Если после этого внесёте наблюдение, слепым оно уже не будет — в сверке строка пойдёт с пометкой.",
    blindBanner: "Слепой ввод: вердикт модели скрыт",
    notBlindBanner: "Вердикт уже показан — это наблюдение пойдёт в сверку как не слепое.",
    observedOn: "Дата осмотра",
    state: "Что фактически было",
    states: {
      standing_crop: "Стоит культура",
      stubble: "Стерня",
      // «Голая почва» → «Открытая почва» (2026-10-09, сверка словаря состояний). Форму
      // заполняет оператор, СРАВНИВАЯ то, что видел, с тем, что сказала карточка, — и две
      // разные вещи он выбирает по словам. Пока здесь стояла «голая», одно и то же состояние
      // звалось в продукте «открытой почвой», а в проверке «голой»: расхождение попадало
      // прямиком в цифру согласия, ради которой форма и существует.
      bare_soil: "Открытая почва",
      sown: "Посеяно",
      not_reached: "Не попал на поле",
    },
    confidence: "Уверенность наблюдателя",
    // Шкала одномерная: не уверен → почти уверен → уверен (gt-confidence-2).
    confidenceLevels: { 1: "не уверен", 2: "почти уверен", 3: "уверен" },
    note: "Комментарий",
    notePlaceholder: "что видно на месте, техника, влажность…",
    submit: "Записать и показать вердикт",
    cancel: "Отменить",
    saving: "Сохранение…",
    saved: "Наблюдение записано",
    // Орган — точная подпись кнопки `submit`; поверхность верификации, «вердикт» законен.
    saveFailed:
      "Наблюдение не записалось. Проверьте связь и нажмите «Записать и показать вердикт» ещё раз.",
    revealTitle: "Что сказала модель",
    noVerdict: "У модели нет вердикта — сверить наблюдение не с чем.",
    existing: (n: number) => `${n} ${plural(n, "наблюдение", "наблюдения", "наблюдений")} по полю`,
    needState: "Выберите, что было на поле",
    // Имена исходов api/truth.py classify_outcome — по одному на значение. Переехали из
    // секции reconcile 2026-09-05, когда экран сверки ушёл с продуктовой поверхности;
    // потребитель — reveal-панель слепой формы (lib/agreement.ts OUTCOME_LABEL), живёт за
    // VERIFICATION_MODE.
    outcomes: {
      match: "Совпало",
      false_positive: "Ложное «да»",
      miss: "Пропуск",
      uncertain: "«Возможно», в счёт не идёт",
      no_verdict: "Вердикта нет",
      // excluded = not_reached (truth.py); «не попал на поле» — дословно states.not_reached.
      excluded: "Не попал на поле — не в счёт",
    },
  },

  // Секция `reconcile` УДАЛЕНА ЦЕЛИКОМ 2026-09-05 (закон зачистки, (правила текстов — ТЗ.md)
  // §16): экран сверки, плитки согласия, доли попаданий и переводы причин «нет вердикта» —
  // методология, которой продукт не носит ни на каком ярусе. Цифра владельца — CSV-экспорт
  // /api/truth/agreement.csv; имена исходов переехали в `groundTruth.outcomes`, перевод
  // no_run — в `dev.noRun`. Слепая форма наблюдений (`groundTruth`) живёт за
  // VERIFICATION_MODE.

  map: {
    // Кнопки зума — Leaflet-овские и по умолчанию несут английские «Zoom in» / «Zoom out»
    // в title. Слова на экране живут в словаре, поэтому контрол создаётся с этими.
    zoomIn: "Приблизить",
    zoomOut: "Отдалить",
  },

  preview: {
    // Регулировки прозрачности нет: растр показывается как есть. Контуры полей читаются
    // потому, что слой превью вставлен ПОД ними, а не потому, что снимок притушили.
    // Горячая клавиша живёт в подписи самой кнопки.
    prev: "Раньше (←)",
    next: "Позже (→)",
    loading: "Снимок загружается…",
    failed: "Снимок не загрузился",
    // «Приблизьте карту» — дословно формула соседнего `imagery.zoomHint` (preview-zoom).
    zoomNote: "На этом масштабе снимок не показывается — приблизьте карту.",
  },

  common: {
    loading: "Загрузка…",
    retry: "Повторить",
    close: "Закрыть",
    // `error` УДАЛЁН 2026-09-05: голая «Ошибка» без подлежащего и без органа не проходит
    // закон зачистки; единственный вызов переведён на `groundTruth.saveFailed`.
    // «Ещё раз» относится к только что сделанному действию — орган контекстный, для
    // catch-all допустимо (unknown-error).
    unknownError: "Неизвестная ошибка. Попробуйте ещё раз.",
    noData: "—",
  },

  draw: {
    // Горячая клавиша написана в подписи: отдельной строкой-подсказкой она бы пересказывала
    // кнопку.
    undo: "Отменить — Ctrl+Z",
    redo: "Вернуть — Ctrl+Shift+Z",
    validation: {
      // Главная из четырёх: бантик рисуется молча, а площадь у него неверная — и на площади
      // держится весь контроль стоимости прогона.
      selfIntersection:
        "Контур пересекает сам себя: площадь посчитается неверно. Пересечения отмечены на карте.",
      duplicatePoints: "Две вершины совпали — лишнюю уберите: правый клик по вершине.",
      tooFewPoints: "Нужно не меньше трёх вершин.",
      outOfRange: "Вершина вне WGS-84: широта ±90°, долгота ±180°.",
    },
  },

  coords: {
    // Одно поле, две ветки: что напечатано, то и ищется. Подпись обязана называть обе.
    label: "Поиск: место или координаты",
    placeholder: "Обнинск · 55.097, 36.610",
    submit: "Найти",
    // Порядок выдачи жёсткий: мгновенное и бесплатное сверху, сетевое снизу.
    groupCoords: "Координаты",
    groupFields: "Поля",
    groupPlaces: "Места",
    searching: "Идёт поиск мест…",
    nothing: "Ничего не найдено",
    // Не «ошибка сети»: важно, что именно уцелело. Координаты работают без сети.
    offline: "Поиск мест недоступен. Координаты работают.",
    fieldArea: (ha: number) => `${ha.toFixed(1)} га`,
    hint: "Широта, потом долгота. Метка временная: полем она не станет и никуда не сохранится.",
    clear: "Убрать метку",
    at: (point: string) => `Метка: ${point}`,
    errors: {
      empty: "Введите координаты",
      unparsed:
        "Не разобрано. Форматы: 55.097, 36.610 · 55.097 36.610 · 55°05'49\"N 36°36'37\"E · N55.097 E36.610",
      needTwo: "Нужны ровно две координаты: широта и долгота",
      latRange: "Широта вне диапазона: от −90° до 90°",
      lonRange: "Долгота вне диапазона: от −180° до 180°",
      // Ровно та ошибка, ради которой стоит отдельная строка: пара валидна, но задом наперёд.
      swapped: "Широта больше 90°: похоже, порядок «долгота, широта» — здесь первой идёт широта",
      // Сообщение сравнялось с валидацией (coords.ts:153): 60 не проходит.
      minutes: "Минуты должны быть от 0 до 59",
      seconds: "Секунды должны быть от 0 до 59",
      // «Ось» — слово геометрии, не человека с координатами из объявления: буквы N/S/E/W он
      // видит, оси — нет (закон зачистки 2026-09-05).
      sameAxis:
        "Обе координаты помечены одной и той же буквой: одна должна быть широтой, другая долготой",
    },
  },

  // ЯРУС 3 целиком. Все три группы чисел живут одним блоком на поверхности разработчика: они
  // написаны для нас. Строк части 2 («NDVI сейчас», «спад от пика») здесь нет — повторять их
  // значило бы выдавать одно число за два.
  evidence: {
    title: "Доказательства",
    optical: "Оптика (Sentinel-2)",
    radar: "Радар (Sentinel-1)",
    decision: "Решение",
    ndmiChange30: "NDMI за 30 дней",
    bsiChange30: "BSI за 30 дней",
    trendSlope: "тренд NDVI, в день",
    kendall: "Кендалл τ",
    filteredAcquisitions: "съёмок после фильтра сцены",
    latestClear: "последняя чистая дата",
    // Единица через запятую — как у соседних readout (evidence-data-age).
    dataAge: "возраст данных, дней",
    robustPoints: "точек",
    // Один ярлык, значение да/нет: ярлык, меняющийся вслед за своим значением, читается как
    // два разных измерения.
    radarStarted: "радар запускался",
    radarTracks: "треков",
    radarAcquisitions: "съёмок радара",
    radarClass: "класс радара",
    // Ненаправленный ярлык величины со знаком — «изменение X»: направление несёт знак числа
    // (evidence-change-labels, глоссарий).
    rfdiChange: "изменение RFDI, п.п.",
    b11Growth: "изменение B11, %",
    risingOrbits: "растущих орбит",
    dryState: "сухое состояние",
    dryDays: "дней сухо",
    eventFrom: "событие с",
    eventTo: "событие по",
    area: "площадь контура",
    analysisArea: "площадь анализа",
    metricCrs: "метрическая проекция",
    innerBuffer: "внутренний буфер применён",
    yes: "да",
    no: "нет",
  },

  // ЯРУС 3. Заголовок и счёт переведены на слова проверки 2026-09-05 (закон зачистки):
  // «отклонённая дата» — слово нашего конвейера, «снимок, не прошедший проверку» — то же
  // множество словами календаря и полосы. Единица счёта — съёмки, как в `strip.summary`;
  // в счёт входят и съёмки без даты.
  rejected: {
    title: "Снимки, не прошедшие проверку",
    none: "Все снимки прошли проверку.",
    count: (n: number) => shots(n),
    reasons: {
      // Процент — с пробелом от числа (percent-spacing, глоссарий).
      LOCAL_BAD_PIXELS_ABOVE_LIMIT: (percent: number | null) =>
        percent === null
          ? "Облако или тень выше предела 10 % площади поля"
          : `Облако или тень на ${percent} % поля (предел 10 %)`,
      VALID_COVERAGE_BELOW_LIMIT: (percent: number | null) =>
        percent === null
          ? "Пригодных пикселей меньше 90 %"
          : `Пригодных пикселей ${percent} % (нужно ≥ 90 %)`,
      NO_FIELD_PIXELS: () => "Поле не попало в снимок: ни одного пикселя внутри контура",
      NO_VALID_INDICES: () => "Индексы не посчитались: нет отражения ни в одном пикселе",
      PROCESSING_ERROR: () => "Ошибка обработки съёмки",
      LOW_VALID_COVERAGE_OR_NO_STATS: () => "Радар: пригодных пикселей мало или статистики нет",
      // «Баг пайплайна» — разговор разработчиков; «отклонения» ушло вместе с заголовком.
      REJECTED_WITHOUT_REASON: () => "Причина не записана: ошибка на нашей стороне.",
      UNKNOWN: () => "Причина неизвестна",
    },
  },

  // ЯРУС 3. Тумблер, значение которого разработчик не может прочитать, хуже спрятанного: он
  // зовёт изменить то, чего никто не предскажет. Поэтому у каждого флага три строки — что он
  // делает сейчас, что будет после переключения и что означает умолчание.
  flags: {
    title: "Флаги прогона",
    ofRun: "флаги этого прогона",
    legacyGrid: "QA_LEGACY_GRID",
    powerDomain: "RADAR_POWER_DOMAIN",
    on: "вкл",
    off: "выкл",
    scope:
      "Переключатели задают флаги следующего прогона. Показанный вердикт они не пересчитывают.",
    ifOn: "Если включить:",
    ifOff: "Если выключить:",
    legacyGridOn:
      "QA считается на сетке EPSG:4326, как в ноутбуке: доля верная, абсолютные счётчики пикселей завышены на 1/cos(широты).",
    legacyGridOff:
      "QA считается на сетке снимка: честные абсолютные счётчики. Отклонённые даты этим не разблокируются.",
    legacyGridDefault:
      "По умолчанию вкл — так считал ноутбук. Флаг и существует ради сравнения с ним, а не ради качества ответа: доля пригодных пикселей верна в обоих положениях, и порог 0.90 достижим тоже в обоих.",
    powerDomainOn: "Радар усредняется в линейной мощности (правка №5).",
    powerDomainOff: "Радар усредняется в децибелах, как в ноутбуке.",
    powerDomainDefault:
      "По умолчанию выкл — так усреднял ноутбук. Флаг и существует ради сравнения с ним.",
    powerDomainScope:
      "Трогает только числа радара. Ряд радара собирается не на каждом прогоне — тогда флаг не меняет ничего.",
  },

  // ЯРУС 3. Прибор владельца. Ничего из продукта здесь не УДАЛЕНО — сюда переехало то, что
  // адресовано нам. Жаргон здесь не смягчается, а уточняется.
  dev: {
    // Не «Отладка» и не «Debug»: на экран смотрит и агроном, а «отладка» обещает поломку.
    title: "Служебные данные",
    hint: "Ctrl+Alt+D",
    open: "Раскрыть служебные данные",
    close: "Свернуть служебные данные",
    about:
      "Прибор, а не продукт: здесь то, чем прогон посчитан и что он вернул дословно. На решение ехать ничего отсюда не влияет.",
    sections: {
      run: "Прогон",
      dates: "Даты",
      numbers: "Числа",
      provenance: "Провенанс",
      reconcile: "Сверка",
    },
    sectionNotes: {
      run: "Чем прогонять: дата, флаги, очередь пакета.",
      dates: "Что прогон выбросил и почему — построчно, по каждой съёмке окна.",
      numbers: "Все измеренные числа прогона, включая те, что на экран продукта не выходят.",
      provenance:
        "Чем подписан показанный вердикт: версия кода, вызовы GEE, проза пайплайна дословно.",
      // Строка не запрещает орган под ней; «глазами» на ярусе 3 несёт противопоставление
      // снимку (dev-note-reconcile).
      reconcile:
        "Запись того, что видели на поле глазами, — для сверки ответов модели с реальностью.",
    },
    runStatus: "статус прогона",
    runError: "ошибка прогона",
    // Балл 0–100 живёт ЗДЕСЬ и больше нигде на экране, и рядом с числом стоит его
    // определение: без определения число читается как проценты, а с определением на
    // продуктовой поверхности оно печатало бы порог правила.
    score: "балл детектора",
    scoreWhat: "нормированный шаг сигнала, 0–100; насыщается вдвое выше порога срабатывания",
    codeVersion: "версия кода",
    opticalAnswer: "что говорила оптика",
    radarSkip: "радар пропущен",
    mainAnswer: "ответ пайплайна дословно",
    why: "почему так решено",
    // Одна формула на все пустые состояния панели и на строку слежения (DevPanel): сюда же
    // 2026-09-05 переехал перевод no_run из удалённой секции reconcile — два текста об одном
    // отсутствии слились в один.
    noRun: "Поле не анализировалось",
    noField: "Поле не выбрано.",
    // Сырые коды, породившие русские заголовки блоков: без них сверить экран с payload
    // нельзя, а сверка — единственная работа этой панели.
    blockCodes: "Коды блоков колонки",
    blockUse: "тип поля",
    blockVeg: "класс состояния",
    blockPosition: "положение к максимуму",
    blockConfidence: "уверенность состояния",
    blockAnswer: "решение",
    // Печать закрывает ЧИСЛА, а не только заголовок: журнал дат и доказательства — такой же
    // канал вердикта, и открыть их из ящика значило бы обойти собственный замер.
    sealed: "Числа и даты запечатаны вместе с состоянием и ответом.",
    // Поверхность разработчика для слоя снимка. Два бокса стоят отдельными строками не для
    // красоты: ровно их совпадение и было ошибкой, из-за которой картинка пропадала при
    // панорамировании.
    imagery: {
      title: "Снимок на дату",
      boxDates: "Бокс дат (30 км)",
      boxTiles: "Бокс тайлов (сетка)",
      grid: (cells: number, area: string) => `ячеек ${cells} · ${area} deg²`,
      rawBox: "сетка не подошла — сырой бокс вида",
      position: (index: number, total: number) => `дата ${index} из ${total}`,
      markCode: "код отметки",
      template: "шаблон тайлов",
      cacheHits: (hits: number) => `попаданий в клиентский кэш: ${hits}`,
      layers: (count: number) => `живых слоёв на карте: ${count}`,
      // Сырой текст ошибки тайлов живёт здесь, а не на продуктовой поверхности: английский
      // хвост «Failed to fetch» с экрана убран (preview-failed).
      lastError: "последняя ошибка слоя",
    },
  },
} as const;

export type GroundTruthStateKey = keyof typeof T.groundTruth.states;
